import { Component } from '@theme/component';
import { resolveCurrentLocation } from '@theme/shopla-current-location';

/* ────────────────────────────────────────────────────────────────────────────
 * Opening-hours status (compact, client-side)
 *
 * A trimmed port of assets/shopla-opening-hours.js — just enough to render a
 * shop-location card's live status ("Open · Closes at 18:00" /
 * "Closed · Opens Monday at 09:00"). Kept self-contained here (no shared module
 * / importmap entry) so the map stays portable; the full weekly-hours dialog
 * still lives in the opening-hours block. Status is computed on the client on
 * purpose: Shopify caches the rendered page, so a server-side "now" would go
 * stale.
 *
 * Times are read in the LOCATION's own IANA timezone: the `timezone` its
 * opening-hours JSON carries, else the one its ISO country code implies
 * (`country_code` on the place document). With neither, the card omits its
 * status line — nothing then says where that shop's clock stands.
 *
 * What it must NOT borrow is the STORE's timezone (data-utc-offset, Liquid's
 * `%z`), which is what this did. That is the merchant admin's setting and says
 * nothing about where a shop stands: with the store set to America/New_York, a
 * Zürich shop open 12:00–18:00 still read "Open" at 19:25 local time. The
 * visitor's own clock is no fallback either — it says where the VISITOR is.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Degrees the viewport box is snapped outward to before listing pins, for maps
 * that list their own (see #listOwnPins). Coarse enough that ordinary panning
 * stays inside one cell — and so costs no request — while still covering the
 * visible area completely.
 */
const PIN_LISTING_GRID = 0.5;

/** How many handles one `/places` lookup may carry. Must not exceed the
 *  service's MAX_PLACE_HANDLES; matches PLACE_LOOKUP_CHUNK in map-search.js. */
const PLACE_LOOKUP_CHUNK = 250;

const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const WEEKDAY_BY_ABBR = { Sun: 'sunday', Mon: 'monday', Tue: 'tuesday', Wed: 'wednesday', Thu: 'thursday', Fri: 'friday', Sat: 'saturday' };
const DAY_MS = 24 * 60 * 60 * 1000;

/** Minutes since midnight for a "HH:MM" string, or null when unparseable. */
function parseMinutes(value) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value ?? '').trim());
  if (!match) return null;
  const minutes = parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
  return Number.isFinite(minutes) ? minutes : null;
}

/**
 * IANA timezone per ISO 3166-1 alpha-2 country code, for locations whose
 * opening hours carry no `timezone` of their own. Mirrors TIMEZONE_BY_COUNTRY
 * in shopla-opening-hours.js.
 *
 * Only countries that observe ONE wall clock across the whole territory belong
 * here, so the code pins the timezone exactly rather than approximating it.
 * Countries spanning several zones (US, CA, AU, RU, BR, MX, …) and those whose
 * islands keep a different clock from the mainland (ES with the Canaries, PT
 * with the Azores) are deliberately absent: for those the country says too
 * little, and a missing entry degrades to "unknown" — honest — rather than to a
 * wrong Open/Closed.
 */
const TIMEZONE_BY_COUNTRY = {
  AD: 'Europe/Andorra', AT: 'Europe/Vienna', BE: 'Europe/Brussels', BG: 'Europe/Sofia',
  CH: 'Europe/Zurich', CZ: 'Europe/Prague', DE: 'Europe/Berlin', DK: 'Europe/Copenhagen',
  EE: 'Europe/Tallinn', FI: 'Europe/Helsinki', FR: 'Europe/Paris', GB: 'Europe/London',
  GR: 'Europe/Athens', HR: 'Europe/Zagreb', HU: 'Europe/Budapest', IE: 'Europe/Dublin',
  IS: 'Atlantic/Reykjavik', IT: 'Europe/Rome', LI: 'Europe/Vaduz', LT: 'Europe/Vilnius',
  LU: 'Europe/Luxembourg', LV: 'Europe/Riga', MC: 'Europe/Monaco', MT: 'Europe/Malta',
  NL: 'Europe/Amsterdam', NO: 'Europe/Oslo', PL: 'Europe/Warsaw', RO: 'Europe/Bucharest',
  SE: 'Europe/Stockholm', SI: 'Europe/Ljubljana', SK: 'Europe/Bratislava', SM: 'Europe/San_Marino',
  VA: 'Europe/Vatican',
};

/** Whether `Intl` knows this timezone name — a typo'd or dropped zone must not
 *  read as valid. */
function isKnownTimeZone(name) {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: name });
    return true;
  } catch (error) {
    return false;
  }
}

/** The IANA timezone a location's wall clock runs on: its own `timezone` when
 *  the hours carry a usable one, else the one its country implies, else null —
 *  "unknown", never a guess. */
function resolveTimezone(data, countryCode) {
  const own = String(data?.timezone ?? '').trim();
  if (own && isKnownTimeZone(own)) return own;
  const byCountry = TIMEZONE_BY_COUNTRY[String(countryCode ?? '').trim().toUpperCase()];
  return byCountry && isKnownTimeZone(byCountry) ? byCountry : null;
}

/** Reads an absolute instant in a given IANA timezone, or null if invalid. */
function zonedParts(date, timeZone) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', weekday: 'short', hourCycle: 'h23',
    }).formatToParts(date);
    const map = {};
    for (const part of parts) map[part.type] = part.value;
    const hour = parseInt(map.hour, 10) % 24; // h23 can emit "24" at midnight
    const minutes = hour * 60 + parseInt(map.minute, 10);
    const weekdayKey = WEEKDAY_BY_ABBR[map.weekday];
    if (!weekdayKey || !Number.isFinite(minutes)) return null;
    return { isoDate: `${map.year}-${map.month}-${map.day}`, minutes, weekdayKey };
  } catch {
    return null;
  }
}

/** "YYYY-MM-DD" for a wall-clock Date (read via UTC fields). */
function isoDateOf(date) {
  return date.toISOString().slice(0, 10);
}

/** A noon-UTC Date for an ISO date string, safe for whole-day stepping. */
function dateFromIso(iso) {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day, 12));
}

/** Weekday key ('monday'…'sunday') for a wall-clock Date. */
function weekdayKeyOf(date) {
  return WEEKDAYS[(date.getUTCDay() + 6) % 7];
}

/** The location's "now" as { isoDate, minutes, weekdayKey }, or null when
 *  neither its hours nor its country settle a timezone. The visitor's own clock
 *  is not a fallback: it says where the visitor is, which is no evidence about
 *  where the shop is. */
function currentMoment(data, countryCode) {
  const timezone = resolveTimezone(data, countryCode);
  if (!timezone) return null;
  return zonedParts(new Date(), timezone);
}

/** The exception covering an ISO date, or null. ISO strings compare chronologically. */
function exceptionFor(data, iso) {
  const exceptions = Array.isArray(data.exceptions) ? data.exceptions : [];
  return exceptions.find((exception) => {
    if (!exception || !exception.from) return false;
    const to = exception.to || exception.from;
    return iso >= exception.from && iso <= to;
  }) || null;
}

/** Effective hours for a date: the exception's hours if any, else weekly. */
function scheduleFor(data, iso, weekdayKey) {
  const exception = exceptionFor(data, iso);
  if (exception) {
    const ranges = !exception.closed && Array.isArray(exception.hours) ? exception.hours : [];
    return { closed: exception.closed || ranges.length === 0, ranges };
  }
  const weekly = data.weekly || {};
  const ranges = Array.isArray(weekly[weekdayKey]) ? weekly[weekdayKey] : [];
  return { closed: ranges.length === 0, ranges };
}

/** Whether the shop is open at `minutes` given a day's schedule. */
function isOpenAt(schedule, minutes) {
  if (schedule.closed) return false;
  return schedule.ranges.some((range) => {
    const opens = parseMinutes(range.opens);
    const closes = parseMinutes(range.closes);
    return opens != null && closes != null && opens <= minutes && minutes < closes;
  });
}

/** First opening within the next 7 days as { dayOffset, isoDate, opens }, or null. */
function nextOpening(data, todayIso, nowMinutes) {
  const reference = dateFromIso(todayIso);
  for (let offset = 0; offset <= 7; offset++) {
    const date = new Date(reference.getTime() + offset * DAY_MS);
    const iso = isoDateOf(date);
    const schedule = scheduleFor(data, iso, weekdayKeyOf(date));
    if (schedule.closed) continue;
    const opensTimes = schedule.ranges
      .map((range) => parseMinutes(range.opens))
      .filter((minutes) => minutes != null && (offset > 0 || minutes > nowMinutes))
      .sort((a, b) => a - b);
    if (opensTimes.length) return { dayOffset: offset, isoDate: iso, opens: opensTimes[0] };
  }
  return null;
}

/** "HH:MM" (24h) for minutes-since-midnight. */
function formatTime(minutes) {
  const hours = String(Math.floor(minutes / 60)).padStart(2, '0');
  const mins = String(minutes % 60).padStart(2, '0');
  return `${hours}:${mins}`;
}

/** Localized long weekday name for an ISO date (rendered at noon UTC). */
function weekdayName(iso, locale) {
  try {
    return new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }).format(dateFromIso(iso));
  } catch {
    return new Intl.DateTimeFormat(undefined, { weekday: 'long', timeZone: 'UTC' }).format(dateFromIso(iso));
  }
}

/** Fills `{name}` placeholders in a label. */
function template(value, replacements) {
  return Object.keys(replacements).reduce((result, key) => result.replace(`{${key}}`, replacements[key]), value);
}

/**
 * Formats a distance in meters for a card tag: "850 m" below 1 km, "17.6 km"
 * above (no decimal from 100 km). Empty string when there is no distance (a
 * shipping search, or a location without one). Mirrors map-search.js.
 *
 * @param {number | null | undefined} meters
 * @returns {string}
 */
function formatDistance(meters) {
  if (typeof meters !== 'number' || !Number.isFinite(meters)) return '';
  if (meters < 1000) return `${Math.round(meters)} m`;
  const km = meters / 1000;
  return km < 100 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`;
}

/**
 * Builds the shop-registration prompt shown under an empty search result —
 * "Own a shop? <a>Register it.</a>" — as a paragraph. The wording and the link
 * come from the search bar (which reads them from the storefront's
 * translations), so this only assembles them; a missing link text falls back to
 * the URL being the whole prompt's only clickable part.
 *
 * Mirrored in map-search.js for its fallback dropdown, with matching class
 * names — see the demo notice for the same split.
 *
 * @param {{ prompt?: string, cta?: string, url: string }} register
 * @param {string} className
 * @returns {HTMLParagraphElement}
 */
function buildRegisterPrompt({ prompt, cta, url }, className) {
  const paragraph = document.createElement('p');
  paragraph.className = className;
  if (prompt) paragraph.appendChild(document.createTextNode(`${prompt} `));
  const link = document.createElement('a');
  link.href = url;
  link.textContent = cta || url;
  paragraph.appendChild(link);
  return paragraph;
}

/**
 * The opening-hours payload as an object, whether it arrives already parsed or
 * as the raw JSON string the places index stores.
 *
 * The two sources of a location card disagree on the shape. The sections'
 * Liquid `data-locations` blob embeds the metafield with `| json`, so it
 * arrives as a nested object. The places index instead stores
 * `shopla.opening-hours` verbatim — the Admin API hands every metafield over as
 * a string — and `/places` passes that string straight through, deliberately,
 * so neither the index nor the service has to know this schema.
 *
 * Normalising at the point of use rather than at each mapping site keeps a
 * future source from having to know about the difference: a card feed that
 * forgets to parse loses its status line silently, which is exactly how the
 * places-fed cards lost theirs.
 *
 * @returns {Record<string, any> | null}
 */
function parseOpeningHours(value) {
  let data = value;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch (error) {
      console.warn('Could not parse the opening hours; omitting the status:', error);
      return null;
    }
  }
  return data && typeof data === 'object' ? data : null;
}

/**
 * A compact open/closed status for a location's opening-hours JSON — an object
 * or the raw JSON string — or null when there are no hours at all, or when
 * neither the hours nor the location's country settle a timezone to read them
 * in (either way the card omits the line, rather than showing a status computed
 * against somebody else's clock).
 *
 * @returns {{ open: boolean, label: string } | null}
 */
function computeOpenStatus(value, { countryCode, locale, labels }) {
  const data = parseOpeningHours(value);
  if (!data) return null;
  const weekly = data.weekly || {};
  const hasWeekly = WEEKDAYS.some((day) => Array.isArray(weekly[day]) && weekly[day].length > 0);
  const hasExceptions = Array.isArray(data.exceptions) && data.exceptions.length > 0;
  if (!hasWeekly && !hasExceptions) return null;

  const moment = currentMoment(data, countryCode);
  if (!moment) return null;

  const { isoDate, minutes, weekdayKey } = moment;
  const today = scheduleFor(data, isoDate, weekdayKey);
  const open = isOpenAt(today, minutes);

  let detail = '';
  if (open) {
    const closes = today.ranges
      .map((range) => ({ opens: parseMinutes(range.opens), closes: parseMinutes(range.closes) }))
      .filter((range) => range.opens != null && range.closes != null && range.opens <= minutes && minutes < range.closes)
      .map((range) => range.closes)
      .sort((a, b) => a - b)[0];
    if (closes != null) detail = template(labels.closesAt, { time: formatTime(closes) });
  } else {
    const next = nextOpening(data, isoDate, minutes);
    if (next) {
      detail = next.dayOffset === 0
        ? template(labels.opensAt, { time: formatTime(next.opens) })
        : template(labels.opensOn, { day: weekdayName(next.isoDate, locale), time: formatTime(next.opens) });
    }
  }

  const word = open ? labels.open : labels.closed;
  return { open, label: detail ? `${word} · ${detail}` : word };
}

/** Chevron glyphs for the carousel arrows. */
const CHEVRON_LEFT = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const CHEVRON_RIGHT = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/** Van icon standing in for the cover image on the "Shipping only" card — it
 *  represents a delivery option rather than a place, so it has no storefront
 *  photo to show. */
const DELIVERY_ICON =
  '<svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6.5h10.5v9H3zM13.5 9.5H17l3 3v3h-6.5z"/><circle cx="7" cy="17" r="1.8"/><circle cx="17" cy="17" r="1.8"/></svg>';

/** Spinner glyph (a 3/4 ring) for the results pane's loading state; spun by CSS. */
const SPINNER_SVG = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false"><path d="M12 3a9 9 0 1 0 9 9" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>';

/** The magnifier shown above an empty result's message. Same drawing as the
 *  search bar's icon-search.svg, with the stroke width fixed rather than read
 *  from the theme's --icon-stroke-width: this markup is injected, not rendered
 *  through Liquid's inline_asset_content. Mirrored in map-search.js. */
const SEARCH_ICON_SVG =
  '<svg viewBox="0 0 20 20" width="28" height="28" fill="none" aria-hidden="true" focusable="false"><circle cx="9" cy="9" r="4.75" stroke="currentColor" stroke-width="1.5"/><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="m12.5 12.5 3.25 3.25"/></svg>';

/** How many products a location's carousel shows / loads per page. Must match
 *  CAROUSEL_PAGE in assets/map-search.js (the bar sets the request `size`). */
const CAROUSEL_PAGE = 3;

/**
 * Whether a carousel-page load from the bar was SUPERSEDED rather than failed:
 * the bar rejects with an AbortError when a newer load replaced it, when the
 * search context changed under it (a keystroke, a mode or location change) or
 * when the query was cleared. A superseded load has no answer, so the carousel
 * keeps what it shows — its spinner, or its current cards — and the results
 * event that follows settles it (see #handleSearchResults / #settleCarousel).
 * Rendering it as an empty page instead is what flashed "No products found"
 * between a keystroke and its results.
 *
 * @param {any} error
 */
const isSuperseded = (error) => error?.name === 'AbortError';

/** Safety cap on how many consecutive all-unrenderable pages the carousel will
 *  auto-skip before it stops (leaving "next" clickable). Guards against a
 *  pathological run of storefront-unavailable products hammering the service. */
const CAROUSEL_MAX_EMPTY_SKIPS = 8;

/** How long deferred pins (`data-pins-deferred` — a collection page waiting
 *  for the bar's idle `/locations` listing) stay hidden before the map fails
 *  open and shows every pin. Only reached when the bar never reports at all
 *  (its own service failures already fail open with `handles: null`). */
const DEFERRED_PINS_TIMEOUT = 8000;


/* ────────────────────────────────────────────────────────────────────────────
 * Initial view
 *
 * Where the map opens is decided once, before it is built, from the
 * locations the section seeded (`data-locations`) — never moved afterwards:
 *   - one location (a shop-location page, a single-location shop): centered
 *     on it at DEFAULT_ZOOM;
 *   - several (a shop with more locations, a collection carrying them): the
 *     bounds of all of them, padded, no closer than FIT_MAX_ZOOM;
 *   - none (the homepage): the visitor's current location at DEFAULT_ZOOM —
 *     the remembered one, else the precise one when already authorized, else
 *     GeoIP (see shopla-current-location.js) — waited for up to
 *     LOCATION_WAIT_MS, then DEFAULT_CENTER.
 * ──────────────────────────────────────────────────────────────────────────── */

const DEFAULT_ZOOM = 13;
/** Zürich — only ever seen when nothing at all could be resolved. */
const DEFAULT_CENTER = [8.542247, 47.371803];
/** Closest a multi-location fit goes: keeps a tight cluster readable and
 *  stays out of the zooms where the basemap gets expensive. */
const FIT_MAX_ZOOM = 14;
const FIT_PADDING = 50;
const LOCATION_WAIT_MS = 5000;

/** CSS size of a pin's logo glyph and of a card's logo badge (see the marker
 *  styles in the map sections and .map-location-card__logo); images are
 *  requested at 2x for high-density screens. */
const MARKER_LOGO_PX = 28;
const CARD_LOGO_PX = 44;

/**
 * A Shopify CDN image URL resized to `width` pixels (the CDN honours a
 * `width` query parameter); any other URL is returned unchanged. The places
 * index stores logos as their original upload, which can be megabytes for
 * a glyph rendered at 28px.
 *
 * @param {string | undefined} url
 * @param {number} width
 * @returns {string}
 */
function sizedImageUrl(url, width) {
  if (!url) return '';
  try {
    const parsed = new URL(url, window.location.origin);
    const isShopifyCdn = parsed.hostname.endsWith('cdn.shopify.com') || parsed.pathname.startsWith('/cdn/shop/');
    if (!isShopifyCdn) return url;
    parsed.searchParams.set('width', String(width));
    return parsed.toString();
  } catch {
    return url;
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * Mapbox GL JS loading
 *
 * The library (roughly 500 KB compressed) and its stylesheet are fetched
 * here, on demand, rather than by <link>/<script> tags in the section
 * markup. A stylesheet tag in the body blocks the paint of everything after
 * it until a third-party origin has answered, and a module <script> tag sits
 * in the document's module execution queue, so every module after it (and
 * DOMContentLoaded) waited for the whole library to download and compile.
 * Loaded from here, neither is on the page's critical rendering path.
 *
 * snippets/mapbox-assets.liquid emits the matching preconnect and preload
 * hints so the bytes still start flowing at parse time, at low priority. The
 * URLs are read back from those hints, so the library version is pinned in
 * the snippet; the constants below are the fallback for a map rendered
 * without it.
 * ──────────────────────────────────────────────────────────────────────────── */

const MAPBOX_GL_VERSION = 'v3.29.0';
const MAPBOX_GL_BASE = `https://api.mapbox.com/mapbox-gl-js/${MAPBOX_GL_VERSION}`;

/** The in-flight (or settled) library load, shared by every map on the page.
 *  @type {Promise<any> | null} */
let mapboxGlLoading = null;

/**
 * The URL of a Mapbox GL asset: the preload hint's, when
 * snippets/mapbox-assets.liquid rendered one, else the built-in default.
 *
 * @param {'script' | 'style'} kind
 * @returns {string}
 */
function mapboxGlAssetUrl(kind) {
  const hint = document.querySelector(`link[data-mapbox-gl="${kind}"]`);
  if (hint instanceof HTMLLinkElement && hint.href) return hint.href;
  return `${MAPBOX_GL_BASE}/${kind === 'style' ? 'mapbox-gl.css' : 'mapbox-gl.js'}`;
}

/**
 * Appends a stylesheet <link> for `href` (once per page) and resolves when it
 * has loaded — or failed: the map still works unstyled, so a missing
 * stylesheet must never hold it back. A script-inserted stylesheet does not
 * block rendering, and `crossorigin="anonymous"` matches the preload hint's
 * request mode so the preloaded bytes are reused.
 *
 * @param {string} href
 * @returns {Promise<void>}
 */
function loadStylesheet(href) {
  return new Promise((resolve) => {
    const links = /** @type {HTMLLinkElement[]} */ ([...document.querySelectorAll('link[rel="stylesheet"]')]);
    if (links.some((link) => link.href === href)) {
      resolve();
      return;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.crossOrigin = 'anonymous';
    link.addEventListener('load', () => resolve(), { once: true });
    link.addEventListener('error', () => resolve(), { once: true });
    document.head.appendChild(link);
  });
}

/**
 * Loads Mapbox GL JS and its stylesheet in parallel, once, and resolves to
 * the `mapboxgl` global — or to null when the library could not be loaded
 * (a content blocker, a network failure), in which case the map stays empty
 * and the next map to connect retries.
 *
 * The library is an IIFE bundle that installs `window.mapboxgl` when run;
 * importing it as a module behaves exactly like the `type="module"` tag it
 * replaces, and the snippet's modulepreload hint for the same URL feeds
 * this import.
 *
 * @returns {Promise<any>}
 */
function loadMapboxGl() {
  if (window.mapboxgl) return Promise.resolve(window.mapboxgl);
  if (!mapboxGlLoading) {
    mapboxGlLoading = Promise.all([import(mapboxGlAssetUrl('script')), loadStylesheet(mapboxGlAssetUrl('style'))])
      .then(() => window.mapboxgl ?? null)
      .catch((error) => {
        console.warn('Mapbox GL JS could not be loaded', error);
        mapboxGlLoading = null;
        return null;
      });
  }
  return mapboxGlLoading;
}

/**
 * A custom element that renders a mapbox map.
 *
 * On a page with the map-search bar it also owns the in-map results UI: the
 * results pane lists a card per shop location that matched the current search
 * (ordered by first appearance in the product results), and a floating product
 * carousel is shown above the selected location's pin. Selecting a location
 * card — or clicking that location's pin while a search is active — opens its
 * carousel; the carousel shows at most CAROUSEL_PAGE products and its right
 * arrow lazily loads more (paginated) via the bar's `loadLocationProducts`.
 * A page still loading shows a spinner in the first card's place — and keeps
 * it when the bar supersedes the load (a keystroke lands mid-fetch) until the
 * results that follow refresh or replace the carousel: "No products found" is
 * only ever a finished load's answer (see isSuperseded).
 * Clicking/tapping outside the carousel closes it — but never the press that
 * starts a map pan.
 *
 * Viewport-filtered (location-mode) results FOLLOW the map: the pane lists
 * only the matched locations whose pin is currently on the map, re-synced
 * after every pan/zoom — a card disappears when its pin is panned out (the
 * bar keeps the data cached) and returns when it is panned back in. The open
 * carousel always tracks the best-matching VISIBLE location: it stays put
 * while it shows the top-ranked result (the highest-scoring product) whose
 * pin is in view, and switches to the new best visible match whenever a
 * higher-ranked location pans in or its own pin leaves the view — opened in
 * place, its card highlighted, without ever recentering the map.
 *
 * An EXPLICIT selection (a card or pin click) overrides that tracking: it
 * holds while its pin stays in view, however the results shift around it.
 * Opening it may recenter the map, and the bounds change re-searches and
 * merges in whatever the new viewport matches — neither that movement nor
 * the results it loads may steal the selection or move the map again; only
 * the visitor panning the selected pin out of view releases it.
 *
 * The pane's card order is APPEND-ONLY within one query+mode: the first
 * render is ranked, and every later render keeps each shown card's slot
 * while newcomers (a pan's merged results, a pin returning to view) join at
 * the end — so neither loading nor re-rendering ever reshuffles the list or
 * moves its scroll position under the visitor (see #renderLocationCards).
 *
 * @typedef {object} Refs
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLElement} [searchResults] - The results pane.
 * @property {HTMLElement} [searchResultsBody] - The results pane body (location cards).
 * @property {HTMLButtonElement} [searchResultsClose] - Collapses the pane.
 * @property {HTMLButtonElement} [searchResultsToggle] - Reopens the collapsed pane.
 *
 * @extends Component<Refs>
 */
export class Mapbox extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Render results from any map-search bar (inside this map, the header, or
    // the fullscreen bar) in the results pane, and track fullscreen state so
    // the fullscreen-only search bar can show.
    const { signal } = this.#controller;
    // All of these are queued until the map exists (see #untilMapReady): the
    // library loads asynchronously, and the bar routinely reports before it
    // has arrived.
    document.addEventListener('shopla:search:results', this.#untilMapReady(this.#handleSearchResults), { signal });
    document.addEventListener('shopla:search:status', this.#untilMapReady(this.#handleSearchStatus), { signal });
    document.addEventListener('shopla:search:cleared', this.#untilMapReady(this.#handleSearchCleared), { signal });
    // The bar drives the pins while no search is ongoing (e.g. a collection
    // page shows only the locations carrying a product of the collection).
    document.addEventListener('shopla:pins:changed', this.#untilMapReady(this.#handlePinsChanged), { signal });
    // The shipping region can resolve mid-search (the geocode races the
    // first keystrokes) — refresh the delivery cards' "Ships to" lines then.
    document.addEventListener('shopla:ships-to:changed', this.#untilMapReady(this.#handleShipsToChanged), { signal });

    // On pages whose idle pins are collection-scoped (`data-pins-deferred` —
    // see the section markup) the pins start HIDDEN: the server renders the
    // unscoped fallback pin set, and showing it while the bar's query-less
    // /locations listing is in flight would flash locations that carry no
    // product of the collection. Deferred only when a bar exists to deliver
    // that listing (either implementation), and failed open — every pin
    // revealed — if none arrives in time.
    if (
      this.hasAttribute('data-pins-deferred') &&
      document.querySelector('map-search-component, shopla-map-search')
    ) {
      this.#pinsPending = true;
      this.#idlePinHandles = new Set();
      setTimeout(() => {
        if (this.#controller.signal.aborted || !this.#pinsPending) return;
        this.#pinsPending = false;
        this.#idlePinHandles = null;
        if (!this.#searchActive) this.#applyPinVisibility();
      }, DEFERRED_PINS_TIMEOUT);
    }
    document.addEventListener('fullscreenchange', this.#syncFullscreen, { signal });
    document.addEventListener('webkitfullscreenchange', this.#syncFullscreen, { signal });
    // Closing the pane collapses it but leaves a toggle to reopen it.
    this.refs.searchResultsClose?.addEventListener('click', this.#collapseResults, { signal });
    this.refs.searchResultsToggle?.addEventListener('click', this.#expandResults, { signal });

    // Lift the mobile results pane above the on-screen keyboard while typing:
    // track the keyboard via the visual viewport (which shrinks when it opens)
    // and expose its overlap with the map's bottom edge as --keyboard-inset,
    // consumed by .map-results on mobile.
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.#updateKeyboardInset, { signal });
      window.visualViewport.addEventListener('scroll', this.#updateKeyboardInset, { signal });
    }
    // A click/tap outside the carousel closes it.
    document.addEventListener('pointerdown', this.#handleDocumentPointerDown, { signal });

    // Labels + locale/offset for the location cards' open/closed status (reused
    // from the opening-hours + pagination strings — see slideshow-map.liquid).
    this.#labels = {
      open: this.dataset.labelOpen || 'Open',
      closed: this.dataset.labelClosed || 'Closed',
      opensAt: this.dataset.labelOpensAt || 'Opens at {time}',
      opensOn: this.dataset.labelOpensOn || 'Opens {day} at {time}',
      closesAt: this.dataset.labelClosesAt || 'Closes at {time}',
      closesOn: this.dataset.labelClosesOn || 'Closes {day} at {time}',
      prev: this.dataset.i18nPrev || 'Previous',
      next: this.dataset.i18nNext || 'Next',
      close: this.dataset.i18nClose || 'Close',
      noResults: this.dataset.i18nNoResults || 'No products found',
      // Deliberately NOT the same string as `noResults`: this one means we
      // could not find out, which is a different thing from finding nothing.
      loadFailed: this.dataset.i18nLoadFailed || 'Couldn’t load products',
      searching: this.dataset.i18nSearching || 'Searching',
      setLocation: this.dataset.i18nSetLocation || 'Set as current location',
      deliveryOnlyTitle: this.dataset.i18nDeliveryOnlyTitle || 'Shipped to you',
      deliveryOnlyBadge: this.dataset.i18nDeliveryOnlyBadge || 'Shipping only',
      shipsTo: this.dataset.i18nShipsTo || 'Ships to {region}',
    };
    this.#locale = this.dataset.locale || document.documentElement.lang || undefined;

    const token = this.getAttribute('mapbox-token');
    if (!token) {
      console.warn('Missing mapbox-token attribute');
      return;
    }

    this.#initMap(token, JSON.parse(this.dataset.locations || '[]'));
  }

  /**
   * Wraps a document-event handler so events that arrive while the library
   * is loading (see loadMapboxGl) are queued and replayed in order once the
   * map exists. The bar's first idle-pin listing routinely beats the
   * library on a warm cache, and #addLocations would otherwise drop its
   * places for want of a map. Only the loading window queues: with no token
   * there is no map to wait for, and the pane answers the bar map-less as
   * before; a failed load releases the queue the same way.
   *
   * @param {(event: Event) => void} handler
   * @returns {(event: Event) => void}
   */
  #untilMapReady(handler) {
    return (event) => {
      if (!this.#mapPending) {
        handler(event);
        return;
      }
      this.#pendingEvents.push(() => handler(event));
    };
  }

  /** Closes the queuing window and runs what it collected, in order. */
  #flushPendingEvents() {
    this.#mapPending = false;
    const pending = this.#pendingEvents;
    this.#pendingEvents = [];
    for (const replay of pending) replay();
  }

  /**
   * Builds the map once Mapbox GL JS is available: the tail of
   * connectedCallback, split off because the library loads asynchronously
   * (see loadMapboxGl). The element may have been disconnected meanwhile, so
   * the controller's signal is re-checked after the await.
   *
   * @param {string} token - The Mapbox public token.
   * @param {any[]} locations - The section's seeded locations (`data-locations`).
   */
  async #initMap(token, locations) {
    const { signal } = this.#controller;
    this.#mapPending = true;
    const mapboxgl = await loadMapboxGl();
    if (signal.aborted || !mapboxgl) {
      this.#flushPendingEvents();
      return;
    }

    const camera = await this.#initialCamera(mapboxgl, locations);
    if (signal.aborted) {
      this.#flushPendingEvents();
      return;
    }

    mapboxgl.accessToken = token;
    const map = new mapboxgl.Map({
        style: this.getAttribute('map-style') || "mapbox://styles/shopla/cmk85drsl005901se308a9qhl",
        container: this,
        ...camera,
        scrollZoom: true,
        // Performance: no post-load POST of render metrics to events.mapbox.com
        // (the documented opt-out; the session/billing event is unaffected), and
        // no re-request of tiles whose cache headers expire while the page stays
        // open — a shop map does not need fresher basemap tiles mid-session.
        performanceMetricsCollection: false,
        refreshExpiredTiles: false,
        // The style imports Mapbox Standard, whose 3D buildings, landmarks and
        // trees load from zoom 14 up — a dozen or more extra tiles and several
        // megabytes on exactly the views the shop map lands on (a fitted pin, a
        // shop-location page). Pins read better over flat buildings anyway.
        // Merged with the style's own import config (theme, light preset).
        config: { basemap: { show3dObjects: false } },
    });
    this.#map = map;
    this.#mapboxgl = mapboxgl;

    // Mapbox measures this container once at construction, but in the map
    // hero the container's height is calc(100svh - header offset) and the
    // header height variables are written several times while the page loads
    // (CSS default → theme.liquid's inline measurement → header.js's
    // ResizeObservers), so the container steps through a few heights before
    // it settles. Mapbox's own trackResize applies each correction on its
    // next render frame, which can paint a stale, shorter canvas first — the
    // transient white band at the hero's bottom. Re-measure the canvas the
    // moment the container's size actually changes (a ResizeObserver callback
    // runs after layout, before paint); unchanged notifications are skipped
    // since every resize() forces a render pass.
    let measuredWidth = this.clientWidth;
    let measuredHeight = this.clientHeight;
    this.#containerResizeObserver = new ResizeObserver(() => {
      if (this.clientWidth === measuredWidth && this.clientHeight === measuredHeight) return;
      measuredWidth = this.clientWidth;
      measuredHeight = this.clientHeight;
      map.resize();
    });
    this.#containerResizeObserver.observe(this);

    // Belt and braces for the same load race: re-measure once the style has
    // fully loaded, and once more a frame later, in case the container
    // reached its final height between the observer's last callback and the
    // first full render.
    map.on('load', () => {
      map.resize();
      requestAnimationFrame(() => {
        if (!this.#controller.signal.aborted) map.resize();
      });
    });

    const geolocateControl = new mapboxgl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true
      },
      trackUserLocation: false,
      showUserHeading: true
    });
    // The control moves the map itself; the fix is also an explicit
    // current-location change for the (decoupled) search bar, which owns the
    // location state.
    geolocateControl.on('geolocate', (position) => {
      const { latitude, longitude } = position?.coords ?? {};
      if (typeof latitude === 'number' && typeof longitude === 'number') {
        this.#publishLocation(latitude, longitude, 'geolocation');
      }
    });
    map.addControl(geolocateControl, 'bottom-right');
    map.addControl(new mapboxgl.FullscreenControl(), 'bottom-right');

    // Right-click opens the contextual menu ("Set as current location") at the
    // clicked point; any map movement, a click on the map, or an outside press
    // dismisses it.
    map.on('contextmenu', (event) => this.#openContextMenu(event));
    map.on('move', () => this.#closeContextMenu());
    // A genuine click on the map base also dismisses the carousel — but never
    // a pan (Mapbox doesn't fire 'click' after a drag) and never a click on a
    // pin, whose own handler just opened/switched the carousel.
    map.on('click', (event) => {
      this.#closeContextMenu();
      const target = event.originalEvent?.target;
      if (target instanceof Element && target.closest('.shopla-marker')) return;
      this.#closeCarousel();
    });
    // On Windows the browser fires `contextmenu` on right-button RELEASE, so
    // finishing a right-drag rotate would pop the menu — remember when a
    // rotation last ended and ignore the menu event right after it.
    map.on('rotateend', () => {
      this.#lastRotateEnd = Date.now();
    });
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') this.#closeContextMenu();
      },
      { signal }
    );

    this.#addLocations(locations);
    // The seeded pins' card metadata comes with the theme bar's idle listing
    // on pages where it lists; only look it up here where it doesn't (a
    // bar-less map, a shop-location page).
    if (!this.#themeBarDrivesPins()) {
      this.#resolveSeededMeta(locations.map((/** @type any */ loc) => loc?.handle).filter(Boolean));
    }
    // The view was decided at construction (#initialCamera): the map is never
    // moved under the visitor afterwards — pins added later arrive because
    // they panned somewhere, and the current location is the initial center,
    // never a later flyTo.

    // Deferred pins: hide the just-created markers before the first paint —
    // they stay hidden until the bar's idle listing (or the failsafe) shows
    // the right ones.
    if (this.#pinsPending) this.#applyPinVisibility();


    // Publish the visible viewport so the decoupled search box can filter to
    // it (its `bb` bounding box). The center is deliberately NOT published —
    // the search location must never be derived from it. `moveend` (not
    // `move`) keeps this to one event per pan/zoom; `load` seeds the initial
    // value.
    const publishBounds = () => this.#publishBounds(map);
    map.on('load', publishBounds);
    map.on('moveend', publishBounds);

    // Keep viewport-filtered results in step with every pan/zoom: the pane
    // lists only the matched locations whose pin is on the map, and an open
    // carousel closes once its pin has been panned out of view. (The
    // debounced re-search the bounds event triggers then merges in whatever
    // the new viewport matches.)
    map.on('moveend', () => this.#syncResultsToViewport());

    // With no search bar on the page, nothing else reports which locations are
    // in view — so the map lists them itself (see #listOwnPins). Checked
    // against the DOM, not the upgraded elements: the tag is in the markup
    // whether or not its module has run yet, exactly as the deferred-pins
    // check above assumes.
    if (!this.hasAttribute('data-pins-fixed') && !document.querySelector('map-search-component, shopla-map-search')) {
      const listOwnPins = () => this.#listOwnPins(map);
      map.on('load', listOwnPins);
      map.on('moveend', listOwnPins);
    }

    // Replay what the bar reported while the library was loading, in order,
    // now that the seeded pins are in place — exactly as if the map had
    // existed all along.
    this.#flushPendingEvents();
  }

  /**
   * Lists the shop locations inside the current viewport straight from the
   * search service, and pins the ones the map doesn't have.
   *
   * Only for maps with no search bar on the page — a slide-map, or a section
   * with its bar switched off — and never on a shop-location page, whose
   * server-rendered single pin is the whole story (`data-pins-fixed`). A map
   * WITH a bar gets its pins from the bar's idle listing instead, which is also
   * collection-scoped where that matters.
   *
   * Without this, a bar-less map would show only the locations its section
   * server-rendered: its own collection's, or its shop's. It used to fall back
   * to the store-wide `all-locations` collection, but that list metafield is
   * capped by Shopify at 128 entries, so a store's 129th location had no pin
   * anywhere. Listing the viewport has no such ceiling.
   *
   * The box is snapped OUTWARD to a coarse grid, so panning within a cell
   * costs no request and an edge pin is never cut off. Best-effort: a failure
   * leaves the map with the pins it already has.
   *
   * @param {import('mapbox-gl').Map} map
   */
  async #listOwnPins(map) {
    const bounds = map.getBounds();
    if (!bounds) return;

    const up = (value) => Math.ceil(value / PIN_LISTING_GRID) * PIN_LISTING_GRID;
    const down = (value) => Math.floor(value / PIN_LISTING_GRID) * PIN_LISTING_GRID;
    const box = [
      Math.min(90, up(bounds.getNorth())),
      Math.max(-180, down(bounds.getWest())),
      Math.max(-90, down(bounds.getSouth())),
      Math.min(180, up(bounds.getEast())),
    ].join(',');
    if (this.#listedPinBoxes.has(box)) return;

    // Only the latest listing matters — panning fires this faster than the
    // service can answer.
    this.#pinListingController?.abort();
    this.#pinListingController = new AbortController();
    const { signal } = this.#pinListingController;

    // Marked covered while in flight, so the load/moveend pair the initial
    // view fires lists it once; a failed listing is unmarked for a retry.
    this.#listedPinBoxes.add(box);
    const places = await this.#fetchPlaces({ type: 'location', bb: box }, signal);
    if (!places) {
      this.#listedPinBoxes.delete(box);
      return;
    }
    this.#addLocations(await this.#withShopFallback(this.#toLocations(places), signal));
    if (signal.aborted) return;
    this.#applyPinVisibility();
  }

  /**
   * Dispatches the map's visible viewport via a bubbling `map:bounds-changed`
   * event for decoupled listeners: the search box's location mode expands it
   * into the `bb` bounding-box filter. (The center is never published — the
   * search location must not derive from it.)
   *
   * @param {import('mapbox-gl').Map} map
   */
  #publishBounds(map) {
    const bounds = map.getBounds();
    if (!bounds) return;
    const detail = {
      north: bounds.getNorth(),
      south: bounds.getSouth(),
      east: bounds.getEast(),
      west: bounds.getWest(),
    };
    // Dispatched under both names: `map:*` for this theme's own listeners,
    // and `shopla:map:*` mirroring the Shopla app's map block so the app's
    // search-bar block can pair with a theme-rendered map.
    for (const type of ['map:bounds-changed', 'shopla:map:bounds-changed']) {
      this.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true, detail }));
    }
  }

  /**
   * Dispatches an explicit current-location change via a bubbling
   * `map:location-changed` event. The search bar owns the location state; the
   * map only reports the visitor's explicit actions — the geolocate control
   * (`'geolocation'`) and the right-click "Set as current location"
   * (`'manual'`).
   *
   * @param {number} lat
   * @param {number} lng
   * @param {'geolocation' | 'manual'} source
   */
  #publishLocation(lat, lng, source) {
    const detail = { lat, lng, source };
    for (const type of ['map:location-changed', 'shopla:map:location-changed']) {
      this.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true, detail }));
    }
  }

  /* ─────────────────────────────────────────────── right-click context menu */

  /**
   * Opens (or moves) the contextual menu at the right-clicked point,
   * remembering the clicked coordinate for its "Set as current location"
   * action. Suppresses the browser's own context menu over the map.
   *
   * @param {import('mapbox-gl').MapMouseEvent} event
   */
  #openContextMenu(event) {
    event.originalEvent?.preventDefault?.();
    // Windows fires `contextmenu` on right-button release — don't pop the menu
    // when that release just ended a right-drag rotation.
    if (Date.now() - this.#lastRotateEnd < 200) return;

    // Wrap into [-180, 180]: panning across the antimeridian yields longitudes
    // beyond it, which the search service would reject.
    const lngLat = typeof event.lngLat?.wrap === 'function' ? event.lngLat.wrap() : event.lngLat;
    this.#contextMenuLngLat = { lat: lngLat.lat, lng: lngLat.lng };

    const menu = this.#ensureContextMenu();
    menu.hidden = false;

    // Clamp inside the map container (measured after unhiding).
    const left = Math.min(event.point.x, Math.max(0, this.clientWidth - menu.offsetWidth));
    const top = Math.min(event.point.y, Math.max(0, this.clientHeight - menu.offsetHeight));
    menu.style.left = `${left}px`;
    menu.style.top = `${top}px`;
  }

  /** Hides the context menu (map movement, outside press, Escape, action). */
  #closeContextMenu() {
    if (this.#contextMenu) this.#contextMenu.hidden = true;
    this.#contextMenuLngLat = null;
  }

  /** Builds the context menu (a single "Set as current location" item) once. */
  #ensureContextMenu() {
    if (this.#contextMenu) return this.#contextMenu;

    const menu = document.createElement('div');
    menu.className = 'map-context-menu';
    menu.hidden = true;
    menu.setAttribute('role', 'menu');

    const item = document.createElement('button');
    item.type = 'button';
    item.className = 'map-context-menu__item';
    item.setAttribute('role', 'menuitem');
    item.textContent = this.#labels.setLocation;
    item.addEventListener('click', () => {
      const lngLat = this.#contextMenuLngLat;
      this.#closeContextMenu();
      if (lngLat) this.#publishLocation(lngLat.lat, lngLat.lng, 'manual');
    });

    menu.appendChild(item);
    this.appendChild(menu);
    this.#contextMenu = menu;
    return menu;
  }

  /* ─────────────────────────────────────────────────── search results (pane) */

  /**
   * Renders a card per matched shop location in the results pane and opens the
   * first location's product carousel. `detail.locations` is the ordered set of
   * matched locations — `[{ handle, distance }]` (first appearance in the
   * product results); only the ones we have a pin for are shown.
   *
   * @param {Event} event
   */
  #handleSearchResults = (event) => {
    if (!(event instanceof CustomEvent)) return;

    const detail = event.detail ?? {};
    // Accept [{ handle, distance }] (current) or [handle] (older); keep only the
    // locations we have a pin for, preserving order — so any the map hasn't
    // pinned yet must be added first, or a genuine match disappears.
    this.#addLocations(detail.places);
    const raw = Array.isArray(detail.locations) ? detail.locations : [];
    const locations = raw
      .map((item) => (typeof item === 'string' ? { handle: item, distance: null } : item))
      .filter((item) => item && this.#locationsByHandle.has(item.handle));
    const handles = locations.map((item) => item.handle);
    const query = typeof detail.query === 'string' ? detail.query : '';

    // The bar that ran this search is the one to call back for a location's
    // products (there may be several bars — header + in-map).
    this.#activeSearch =
      event.target && typeof (/** @type any */ (event.target).loadLocationProducts) === 'function'
        ? /** @type any */ (event.target)
        : null;
    this.#resultHandles = handles;
    this.#resultLocations = locations;
    // How many delivery-only products matched (blank shop_location_handle —
    // no pickup location anywhere, so nothing to pin). The bar only sends the
    // count; their cards are pulled lazily through `loadDeliveryProducts`.
    // One entry per shop with delivery-only matches — one card each, at the
    // rank the bar computed among the location cards (see #renderLocationCards).
    this.#deliveryShops = Array.isArray(detail.delivery)
      ? detail.delivery.filter((shop) => shop && typeof shop.handle === 'string')
      : [];
    // Each entry carries the branding the bar looked up for that shop (null
    // when the service knows nothing about it). Cached rather than read per
    // render, so a re-render triggered by a pan — which re-emits the same
    // results — keeps the cards branded.
    for (const { handle, shop } of this.#deliveryShops) {
      if (shop && typeof shop.title === 'string') this.#shopsByHandle.set(handle, shop);
    }
    // Location-mode results are viewport-filtered and follow the map; shipped
    // results are not coupled to it. An older bar without `mode` follows.
    this.#resultsFollowViewport = detail.mode !== 'shipping';
    // In shipping mode the cards show "Ships to {region}" where a location
    // card normally shows its opening-hours status (see #buildCardStatusLine).
    // A mode switch replaces the result set wholesale (the bar empties its
    // caches), so the pane's append-only order starts over with it.
    const searchMode = detail.mode === 'shipping' ? 'shipping' : 'location';
    if (searchMode !== this.#searchMode) this.#displayOrder.clear();
    this.#searchMode = searchMode;
    if (typeof detail.shipsToRegion === 'string') this.#shipsToRegion = detail.shipsToRegion;
    if (typeof detail.shipsToZone === 'string') this.#shipsToZone = detail.shipsToZone;
    // With a search ongoing only the matched locations keep their pin on the
    // map — applied even without a results pane (e.g. a slide-map).
    this.#searchActive = true;
    this.#resultHandleSet = new Set(handles);
    // The bar owns the empty-state copy — it knows the search mode, and the
    // same wording has to serve its own fallback dropdown. Cached here because
    // the pane can fall empty later in the search too (a pan that takes the
    // last matched pin off screen), long after the event that carried it.
    this.#noteEmptyState(detail);
    // A new query re-enables auto-open — a merchant's close only holds for the
    // query it was closed on.
    this.#noteQuery(query);
    this.#applyPinVisibility();

    const openCarousel = this.#carouselState;
    const stillMatches =
      openCarousel &&
      (openCarousel.kind === 'delivery'
        ? this.#deliveryShops.some((shop) => shop.handle === openCarousel.shopHandle)
        : handles.includes(openCarousel.handle));

    const { searchResults, searchResultsBody } = this.refs;
    if (!searchResults || !searchResultsBody) {
      // No pane (e.g. a slide-map), where only a pin click opens a carousel.
      // One holding its spinner for these results (its page load superseded
      // in the bar) is settled all the same — or closed, if they no longer
      // cover it; the rest is left to the pin and the close button, as ever.
      if (openCarousel?.superseded) {
        if (stillMatches) this.#settleCarousel(openCarousel, query, { center: false });
        else this.#closeCarousel();
      }
      return;
    }

    searchResults.classList.remove('is-loading');
    if (!this.#resultsUserClosed) this.#openResults();

    if (!this.#activeSearch || (handles.length === 0 && this.#deliveryShops.length === 0)) {
      // Products matched, but none at a location we can place on this map and
      // none deliverable either (or no bar to load their products from — don't
      // leave phantom cards for the viewport re-sync to resurrect).
      this.#resultLocations = [];
      this.#deliveryShops = [];
      this.#showPaneEmpty();
      this.#closeCarousel();
      return;
    }

    this.#renderVisibleLocations();

    const reason = typeof detail.reason === 'string' ? detail.reason : 'query';

    if (reason === 'pan') {
      // A pan never recenters the map — it only keeps the carousel on the
      // best-matching VISIBLE location (see #syncCarouselToBestVisible). The
      // carousel it keeps may still owe these results a refresh, though: a
      // pan's search supersedes an unfinished keystroke's (or mode switch's)
      // in the bar, so these can be the first results for that query — and a
      // carousel whose page load the bar aborted is waiting on exactly this
      // event. Settled as a query result would be, minus the recentering.
      this.#syncCarouselToBestVisible();
      if (openCarousel && this.#carouselState === openCarousel) {
        this.#settleCarousel(openCarousel, query, { center: false });
      }
      return;
    }

    if (stillMatches) {
      // The open carousel still matches: keep it, refreshed or rebuilt as
      // these results require (see #settleCarousel) — we never yank the
      // visitor back to the first location.
      this.#settleCarousel(openCarousel, query);
    } else {
      // A fresh search (or the open carousel no longer matches). LOCAL
      // PRIORITY: a product with a physical location inside the map's box wins
      // over any delivery-only product, however well the latter matches — so
      // the delivery carousel opens only when nothing local is in view.
      //
      // The last branch is the pre-delivery behaviour, kept for the case that
      // has no delivery results to arbitrate against: open the top-ranked
      // match even if its pin is currently off-screen. Only a search that
      // actually has a delivery alternative gets the stricter in-view test.
      const bestLocated = this.#bestLocatedHandle();
      if (bestLocated) this.#openCarousel(bestLocated);
      else if (this.#deliveryShops.length > 0) this.#openDeliveryCarousel(this.#deliveryShops[0].handle);
      else if (handles.length > 0) this.#openCarousel(handles[0]);
      else this.#closeCarousel();
    }
  };

  /**
   * Brings a carousel that survived a results event up to date with it.
   *
   * A same-query, same-MODE re-emit keeps the visitor's exact carousel + page;
   * a query change (a keystroke refinement) refreshes that same carousel in
   * place (no blink).
   *
   * A carousel whose page load was SUPERSEDED refreshes too, whatever the
   * query: the bar aborts a carousel load whenever the search context changes
   * (a keystroke, a new current location, a shipping-zone update) and this
   * event is the outcome of that change — the carousel has held its spinner
   * (or its previous cards) for it, and nothing else would ever load it.
   *
   * A MODE change (Pickup ↔ Shipping) with the same query reopens the same
   * carousel from scratch instead: its cards were rendered for the other mode
   * — pickup cards link into the shop-location collection and carry distance
   * tags, delivery cards keep the bare /products/ URL and don't — and the
   * in-place refresh reuses card DOM by product id, which would carry those
   * stale links across. A fresh open rebuilds page 0 from the bar, whose
   * caches the mode switch has already emptied. The reopen keeps the
   * carousel's user-selected standing — it is the same selection, rebuilt for
   * the other mode's cards.
   *
   * @param {any} openCarousel - The open carousel (see #carouselState), which
   *   these results still cover.
   * @param {string} query - The query behind the results.
   * @param {{ center?: boolean }} [options] - `center: false` (a pan-driven
   *   settle) keeps a mode reopen from moving the map.
   */
  #settleCarousel(openCarousel, query, { center = true } = {}) {
    if (openCarousel.mode !== this.#searchMode) {
      if (openCarousel.kind === 'delivery') {
        this.#openDeliveryCarousel(openCarousel.shopHandle, { user: openCarousel.userSelected === true });
      } else {
        this.#openCarousel(openCarousel.handle, { center, user: openCarousel.userSelected === true });
      }
    } else if (openCarousel.query !== query || openCarousel.superseded) {
      this.#refreshCarousel(openCarousel);
    } else {
      this.#reflectActiveCard(openCarousel);
    }
  }

  /**
   * The handle of the best-ranked matched location the carousel may open on, or
   * `null` when there is none — the "is there anything local?" test behind
   * local priority.
   *
   * In location mode the results are already scoped to the map's expanded box
   * server-side (`bb`), so the top-ranked match qualifies as-is; narrowing
   * further to the strictly-visible pins here would change long-standing
   * behaviour for a mode that has no delivery results to arbitrate against.
   * In delivery mode nothing bounds the results geographically, so "local"
   * has to mean what the visitor can actually see: a pin inside the viewport.
   */
  #bestLocatedHandle() {
    if (this.#resultsFollowViewport) return this.#resultHandles[0] ?? null;
    return this.#resultLocations.find(({ handle }) => this.#pinInView(handle))?.handle ?? null;
  }

  /**
   * Renders the pane for the current viewport: of all matched locations, only
   * the ones whose pin is on the map right now get a card (unless the results
   * don't follow the viewport — shipped mode). Panning a pin out removes its
   * card — the bar keeps its data cached, so panning it back in restores the
   * card (and its carousel products) without a refetch.
   */
  #renderVisibleLocations() {
    const visible = this.#resultsFollowViewport
      ? this.#resultLocations.filter(({ handle }) => this.#pinInView(handle))
      : this.#resultLocations;
    if (visible.length === 0 && this.#deliveryShops.length === 0) {
      this.#showPaneEmpty();
      return;
    }
    this.#renderLocationCards(visible);
  }

  /**
   * Whether a shop location's pin currently sits inside the map viewport.
   * `true` while the map can't tell yet (no bounds) so results aren't
   * spuriously hidden before the first render.
   *
   * @param {string} handle
   */
  #pinInView(handle) {
    const location = this.#locationsByHandle.get(handle);
    if (!location) return false;
    const bounds = this.#map?.getBounds();
    return !bounds || bounds.contains(location.coords);
  }

  /**
   * After a pan/zoom: for viewport-filtered results, re-renders the pane so it
   * lists exactly the matched locations now in view; then, for any results,
   * keeps the carousel on the best visible match — or hands over to the
   * delivery carousel when nothing local is in view.
   */
  #syncResultsToViewport() {
    if (this.#resultLocations.length === 0 && this.#deliveryShops.length === 0) return;
    // Only viewport-filtered results re-render their cards on a pan. Shipping-mode
    // results are not geographically scoped, so their card list stays put —
    // but the CAROUSEL is re-evaluated either way, because which carousel
    // belongs on screen depends on whether anything local is in view.
    if (this.#resultsFollowViewport) this.#renderVisibleLocations();
    this.#syncCarouselToBestVisible();
  }

  /**
   * Keeps the carousel on the best-matching VISIBLE location as the map pans.
   * The open carousel stays put only while it shows that location — the
   * top-ranked result (the highest-scoring product) whose pin is currently on
   * the map. When a higher-ranked location pans into view, or the open one's
   * pin leaves the view, the carousel switches to the best visible match and
   * highlights its card (via #openCarousel → #reflectActiveCard), without
   * ever moving the map. With nothing matched in view it closes — a pan-out,
   * not a dismissal — and reopens the best match once one returns.
   *
   * An explicitly selected carousel (a card or pin click — `userSelected`) is
   * exempt while its pin is in view: selecting a card can recenter the map,
   * and both the moveend of that programmatic ease and the pan re-search it
   * triggers (which merges the new viewport's matches into the results) land
   * here — where, without the exemption, they would immediately hand the
   * carousel back to the best-ranked visible location, overriding the click.
   * Only the visitor panning the selected pin out of view releases the
   * selection back to best-visible tracking. A user-selected DELIVERY
   * carousel has no pin to leave the view and is not viewport-coupled at
   * all, so it holds until dismissed or replaced.
   */
  #syncCarouselToBestVisible() {
    const open = this.#carouselState;
    if (open?.userSelected) {
      if (open.kind === 'delivery' || this.#pinInView(open.handle)) return;
      // The visitor panned the selected pin out — the selection goes with it,
      // and from here the carousel follows the best visible match again.
      open.userSelected = false;
    }

    const best = this.#resultLocations.find(({ handle }) => this.#pinInView(handle)) ?? null;

    if (!best) {
      // Nothing local is in view. With delivery-only matches to fall back on,
      // local priority no longer applies and the centred carousel takes over
      // (that is the pan-driven form of the same rule that picks it on a fresh
      // search). Otherwise close, and remember to reopen when a pin returns.
      if (this.#deliveryShops.length > 0) {
        if (open?.kind !== 'delivery') this.#openDeliveryCarousel(this.#deliveryShops[0].handle);
        return;
      }
      if (open) {
        this.#closeCarousel();
        this.#reopenBestOnPan = true;
      }
      return;
    }

    if (open) {
      // Switch when the open carousel is no longer the best visible match — a
      // higher-ranked location panned in, or its own pin left the view. A
      // delivery carousel always yields to a local match that pans into view:
      // local priority reasserts itself the moment there is something local.
      if (open.kind === 'delivery' || open.handle !== best.handle) {
        this.#openCarousel(best.handle, { center: false });
      }
    } else if (this.#reopenBestOnPan) {
      // A pan-out closed the carousel earlier; a match is back in view — reopen.
      this.#openCarousel(best.handle, { center: false });
    }
  }

  /**
   * Shows a status message (loading / empty / error) in the results pane. An
   * empty/error result closes any open carousel; a "loading" status leaves it up
   * (the imminent results will refresh or replace it) to avoid a flash while the
   * visitor types.
   *
   * @param {Event} event
   */
  #handleSearchStatus = (event) => {
    if (!(event instanceof CustomEvent)) return;

    // `noResults` marks a genuine empty result — `state: 'empty'` also carries
    // the "open the map to search" hint, which is not one. `register` rides
    // along with it when the storefront configured a registration link.
    const { state, text, query, demo, noResults, register } = event.detail ?? {};
    // A new query re-enables auto-open even for a status-only outcome (e.g. an
    // empty result set, which arrives as a status event, not results).
    this.#noteQuery(query);
    // A status event only fires while a query is active. A genuinely empty
    // result set — and the demo-storefront notice, which likewise shows no
    // results — means no location matched, so no pin shows. Loading and error
    // keep the current pins: a transient failure must not blank the map.
    if (state === 'empty' || state === 'demo') {
      this.#searchActive = true;
      this.#resultHandles = [];
      this.#resultLocations = [];
      this.#deliveryShops = [];
      this.#resultHandleSet = new Set();
      this.#applyPinVisibility();
    }

    // Empty / error / demo: the carousel goes, pane or no pane (a slide-map's
    // pin click opens one too) — the search it belonged to is over, its pin
    // may be gone with the results, and one still holding its spinner for a
    // superseded page load would otherwise hold it for good.
    if (state !== 'loading') this.#closeCarousel();

    const { searchResults, searchResultsBody } = this.refs;
    if (!searchResults || !searchResultsBody) return;

    if (state === 'loading') {
      // Keep the current location cards (and the carousel) visible during a
      // background reload — no blink. Only show the spinner on the first search,
      // when the pane has nothing yet.
      if (!searchResultsBody.querySelector('.map-locations')) this.#showPaneSpinner();
    } else if (state === 'demo') {
      // Demo storefront (staging): show the "try it live" notice in place of
      // the results a real query would return. See map-search.js #showDemoNotice.
      this.#showPaneDemoNotice(demo ?? {});
    } else {
      // empty / error: replace the pane with the message card.
      if (noResults) this.#showPaneNoResults(text ?? '', register ?? null);
      else this.#showPaneMessage(text ?? '');
    }
    searchResults.classList.toggle('is-loading', state === 'loading');
    // Respect a merchant-closed pane for this query — don't reopen it on a
    // background reload or a same-query empty/error.
    if (!this.#resultsUserClosed) this.#openResults();
  };

  /** Empties and hides the results pane and its reopen toggle (search cleared). */
  #handleSearchCleared = () => {
    // The search no longer owns the pins — fall back to the idle filter (the
    // bar re-lists the collection-scoped set right after clearing).
    this.#searchActive = false;
    // The query is gone — a fresh one starts with the pane free to auto-open.
    this.#resultsUserClosed = false;
    this.#resultHandleSet = null;
    this.#resultHandles = [];
    this.#resultLocations = [];
    this.#deliveryShops = [];
    this.#displayOrder.clear();
    this.#applyPinVisibility();

    const { searchResults, searchResultsBody, searchResultsToggle } = this.refs;
    if (!searchResults || !searchResultsBody) return;

    this.#closeCarousel();
    searchResultsBody.replaceChildren();
    searchResults.classList.remove('is-loading');
    searchResults.classList.remove('is-open');
    if (searchResultsToggle) searchResultsToggle.hidden = true;
  };

  /**
   * Applies a shipping-region change from the bar
   * (`shopla:ships-to:changed`). The region usually resolves asynchronously —
   * a reverse geocode racing the visitor's first keystrokes — so cards built
   * before it landed miss their "Ships to {region}" line; re-rendering the
   * pane reconciles them in place (see #renderLocationCards' update hooks).
   *
   * ONLY when the event's zone matches the zone the current results were
   * filtered by, though. A shipping-address switch changes the zone and the
   * region name in one action, and the refreshed results are still in flight
   * while this event lands — relabeling the old zone's cards with the new
   * region would claim, for however long the reload takes, that those shops
   * ship somewhere they were never checked against. The stale cards keep
   * their (correct) old label; the results event replaces cards, region and
   * zone together.
   *
   * @param {Event} event
   */
  #handleShipsToChanged = (event) => {
    if (!(event instanceof CustomEvent)) return;
    const { region, zone } = event.detail ?? {};
    if (typeof region !== 'string' || region === this.#shipsToRegion) return;
    if (typeof zone === 'string' && zone && this.#shipsToZone && zone !== this.#shipsToZone) return;

    this.#shipsToRegion = region;
    if (this.#searchActive && this.#searchMode === 'shipping') this.#renderVisibleLocations();
  };

  /* ─────────────────────────────────────────────────────────── pin visibility */

  /**
   * Applies the bar's idle pin set (`shopla:pins:changed`): the shop-location
   * handles allowed while no search is ongoing — a collection page's
   * "locations carrying a product of the collection" — or null for "every pin"
   * (pages without a collection context). Ignored while a search is active;
   * it takes effect again when the search clears.
   *
   * @param {Event} event
   */
  #handlePinsChanged = (event) => {
    if (!(event instanceof CustomEvent)) return;
    const { handles, places } = event.detail ?? {};
    this.#pinsPending = false; // the bar reported — deferred pins resolve now
    // Pin whatever the listing turned up that the map hasn't seen, BEFORE the
    // filter is applied — a handle with no pin is otherwise silently dropped.
    this.#addLocations(places);
    this.#idlePinHandles = Array.isArray(handles)
      ? new Set(handles.filter((handle) => typeof handle === 'string'))
      : null;
    if (!this.#searchActive) this.#applyPinVisibility();
  };

  /**
   * One `/places` request, as an array of place documents.
   *
   * Returns null for "the service did not answer" — a failure, or an abort —
   * as distinct from the empty array that means "the service knows no such
   * place". Callers rely on that difference: a box is only marked covered, and
   * a shop only cached as a miss, once the service actually answered.
   *
   * @param {Record<string, string>} params
   * @param {AbortSignal} [signal]
   * @returns {Promise<Record<string, any>[] | null>}
   */
  async #fetchPlaces(params, signal) {
    const base = this.dataset.searchUrl;
    const shop = this.dataset.shop;
    if (!base || !shop) return null;

    try {
      const url = new URL(`${base.replace(/\/+$/, '')}/places`);
      url.searchParams.set('s', shop);
      for (const [key, value] of Object.entries(params)) url.searchParams.set(key, value);

      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error(`shopla-search responded ${response.status}`);
      const body = await response.json();
      if (signal?.aborted) return null;
      return Array.isArray(body) ? body : [];
    } catch (error) {
      if (signal?.aborted) return null;
      console.warn('Places lookup failed:', error);
      return null;
    }
  }

  /**
   * Fills in a location's branding from its shop: one with no logo or cover of
   * its own shows its shop's.
   *
   * The fallback is resolved here rather than denormalised into the index,
   * where a shop changing its image would leave every one of its locations
   * stale — the same reason #resolveLocations in map-search.js resolves it on
   * the client. It used to be computed in Liquid for the locations a section
   * seeded, which is why it has to be done here now that a section no longer
   * describes a whole card.
   *
   * Shops are cached across calls, and a shop the index doesn't know is cached
   * as a miss so it isn't asked for again. A failed lookup is NOT cached, so it
   * is retried rather than leaving a location unbranded for the life of the
   * page.
   *
   * @param {Record<string, any>[]} locations
   * @param {AbortSignal} [signal]
   * @returns {Promise<Record<string, any>[]>}
   */
  async #withShopFallback(locations, signal) {
    const missing = [
      ...new Set(
        locations
          .filter((location) => !location.logo || !location.cover)
          .map((location) => location.shop_handle)
          .filter((handle) => handle && !this.#shopPlaces.has(handle))
      ),
    ];

    for (let i = 0; i < missing.length; i += PLACE_LOOKUP_CHUNK) {
      const chunk = missing.slice(i, i + PLACE_LOOKUP_CHUNK);
      const shops = await this.#fetchPlaces({ type: 'shop', handle: chunk.join(',') }, signal);
      if (!shops) break;
      for (const shop of shops) {
        if (shop && typeof shop.handle === 'string' && shop.handle) this.#shopPlaces.set(shop.handle, shop);
      }
      for (const handle of chunk) {
        if (!this.#shopPlaces.has(handle)) this.#shopPlaces.set(handle, null);
      }
    }

    return locations.map((location) => {
      const shop = this.#shopPlaces.get(location.shop_handle);
      if (!shop) return location;
      return {
        ...location,
        logo: location.logo || shop.logo || '',
        cover: location.cover || shop.cover || '',
      };
    });
  }

  /**
   * Translates the search service's place documents into the shape the map
   * pins and the cards render from: the service speaks `lat`/`lon` and
   * `opening_hours`, the cards speak `lng`, `openingHours` and `countryCode`.
   *
   * The renames are spelled out rather than left to the spread because this is
   * NOT the only feed: the search bar builds its own location objects from the
   * same place documents (#resolveLocations in map-search.js) and hands them
   * straight to #addLocations, so a card must find the same names whichever
   * side it came from. A field only this side happened to spread through was
   * simply absent on every bar-fed card — which is how the country code went
   * missing after the index started carrying it.
   *
   * @param {unknown} places
   * @returns {Record<string, any>[]}
   */
  #toLocations(places) {
    return (Array.isArray(places) ? places : []).map((place) => ({
      ...place,
      lng: place?.lon,
      openingHours: place?.opening_hours,
      countryCode: place?.country_code,
    }));
  }

  /**
   * Folds a later description of an already-pinned location into the one the
   * map holds.
   *
   * A location reaches the map from two directions, and only one of them can
   * arrive at first paint. The section serialises identity, coordinates and the
   * marker glyph for the locations the PAGE is about — enough to place those
   * pins and centre the map with no round-trip — and the places index carries
   * everything a card shows. Keeping the card's fields in one source is the
   * point: when the section described a whole card too, the two descriptions
   * drifted (the index stores the opening hours as a raw JSON string, Liquid
   * embedded them as an object) and the seeded cards quietly rendered from a
   * different shape than every other card on the same map.
   *
   * Only present values are copied, so a source that doesn't carry a field —
   * an empty `cover`, a location with no opening hours — can't blank one the
   * other filled. Identity and geometry are fixed when the pin is placed;
   * re-reading them here would let a later answer move a marker out from under
   * the visitor. `selected` is the page's own answer to "which pin is this page
   * about", which no other source is entitled to.
   *
   * @param {{ handle: string, meta: Record<string, any>, marker: any }} entry
   * @param {Record<string, any>} incoming
   */
  #mergeLocationMeta(entry, incoming) {
    let changed = false;
    for (const [key, value] of Object.entries(incoming)) {
      if (key === 'handle' || key === 'lat' || key === 'lng' || key === 'selected') continue;
      if (value == null || value === '' || entry.meta[key] === value) continue;
      entry.meta[key] = value;
      changed = true;
    }
    if (!changed) return;

    // The glyph is the only part of a pin drawn before its card exists.
    const glyph = entry.marker?.getElement?.()?.querySelector('.shopla-marker-img');
    const glyphSrc = sizedImageUrl(entry.meta.logo, MARKER_LOGO_PX * 2);
    if (glyph && glyphSrc && glyph.src !== glyphSrc) glyph.src = glyphSrc;

    // A card opened before this arrived is showing the gaps. Rebuild it — but
    // only while the pane is still showing that one idle card, never over
    // search results, a message or the spinner.
    const cards = this.refs.searchResultsBody?.querySelectorAll('.map-location-card') ?? [];
    if (!this.#searchActive && cards.length === 1 && cards[0] instanceof HTMLElement && cards[0].dataset.handle === entry.handle) {
      this.#showLocationCard(entry.handle);
    }
  }

  /**
   * Fetches what the section could not render for the pins it seeded.
   *
   * Deliberately independent of the search bar: a shop-location page has no
   * idle listing to piggyback on (`data-pins-fixed` also stops #listOwnPins),
   * and a bar-less map's own listing is viewport-driven, which is not the same
   * set as "the locations this page is about".
   *
   * Best-effort — a failure leaves the seeded pins on the map with the little
   * the section gave them, which is still a pin in the right place.
   *
   * @param {string[]} handles
   */
  async #resolveSeededMeta(handles) {
    if (!handles.length) return;

    const { signal } = this.#controller;
    for (let i = 0; i < handles.length; i += PLACE_LOOKUP_CHUNK) {
      const places = await this.#fetchPlaces(
        { type: 'location', handle: handles.slice(i, i + PLACE_LOOKUP_CHUNK).join(',') },
        signal
      );
      if (!places) return;
      this.#addLocations(await this.#withShopFallback(this.#toLocations(places), signal));
      if (signal.aborted) return;
    }
  }

  /**
   * Pins every location the map doesn't already have, keyed by handle, and
   * keeps its card metadata for the results pane and popups.
   *
   * Idempotent: a handle already pinned never gets a second marker. The same
   * location arriving again — from a later viewport listing, from search
   * results, or because a pan re-covered a box — is merged into the entry the
   * map holds instead of being dropped (see #mergeLocationMeta), which is how
   * a pin the section could only seed picks up what its card shows.
   *
   * Called once at init with the section's server-rendered context locations
   * (its own collection's, or its shop's), then with whatever the search bar
   * reports. That incremental path is the point: the section's payload used to
   * be the *only* source, and it was walked off the store-wide
   * `all-locations` collection, whose list metafield Shopify caps at 128
   * entries — so a store's 129th location had no pin, and search results
   * referencing it were dropped for want of one (see #handleResults). Pins now
   * arrive as the viewport reveals them, with no ceiling.
   *
   * Deliberately does NOT apply the pin filter or move the map — callers do
   * both, since what should happen next differs per call site.
   *
   * @param {any[]} list - locations as `{ handle, lng, lat, logo, title, … }`.
   * @returns {number} how many pins were added.
   */
  #addLocations(list) {
    const map = this.#map;
    const mapboxgl = window.mapboxgl;
    if (!map || !mapboxgl || !Array.isArray(list)) return 0;

    // Enforced here rather than by the caller so that EVERY source of pins is
    // covered — the section payload, the bar's idle listing, search results and
    // #listOwnPins alike — and so a future source is covered by construction.
    const demoAllow = this.#demoAllowList();

    let added = 0;
    for (const location of list) {
      const handle = location?.handle;
      // A location with no handle can't be addressed by the bar or the pane.
      if (!handle || typeof handle !== 'string') continue;
      // Demo gate: never pin a location outside the demoed shop.
      if (demoAllow && !demoAllow.has(handle)) continue;

      // Already pinned: a second marker would be wrong, but so is discarding
      // what this source carries. The section seeds a pin with only what it can
      // render at first paint; `/places` follows with what the card shows.
      const existing = this.#locationsByHandle.get(handle);
      if (existing) {
        this.#mergeLocationMeta(existing, location);
        continue;
      }

      const lng = Number(location.lng);
      const lat = Number(location.lat);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;

      const div = document.createElement('div');
      const img = document.createElement('img');
      div.appendChild(img);
      div.className = 'shopla-marker' + (location.selected ? ' shopla-marker-selected' : '');
      img.className = 'shopla-marker-img';
      // Sized for the 28px glyph (2x for high-density screens) instead of the
      // original upload, decoded off the main thread and only fetched once the
      // pin is near the viewport — hundreds of pins otherwise mean hundreds of
      // full-size logo downloads competing with the page.
      img.width = MARKER_LOGO_PX;
      img.height = MARKER_LOGO_PX;
      img.loading = 'lazy';
      img.decoding = 'async';
      img.src = sizedImageUrl(location.logo, MARKER_LOGO_PX * 2);

      const marker = new mapboxgl.Marker(div, { anchor: 'bottom' }).setLngLat([lng, lat]).addTo(map);

      // Track the marker by handle so the search results can open a carousel
      // above the right pin. While a search is live a (matched) pin opens its
      // product carousel; with no search ongoing a pin shows its shop's card
      // in the results pane instead.
      this.#locationsByHandle.set(handle, { handle, coords: [lng, lat], meta: location, marker });
      div.addEventListener('click', () => {
        if (this.#searchActive) {
          if (this.#resultHandles.includes(handle)) this.#openCarousel(handle, { user: true });
          return;
        }
        this.#showLocationCard(handle);
      });
      added++;
    }
    return added;
  }

  /**
   * The shop-location handles the demo gate permits pinning, or null when the
   * page is not demo-gated at all.
   *
   * A staging demo prospect must only ever see their OWN shop's locations. The
   * gate used to enforce that by rewriting each map's `data-locations`
   * attribute before this component upgraded (see demo-staging.liquid in
   * shopla-shopify) — which worked while that attribute was the only source of
   * pins. It no longer is: pins now also arrive from the search service, both
   * through the bar's idle listing and through #listOwnPins, and an attribute
   * rewrite cannot reach either. So the allow-list is enforced HERE instead,
   * at the one place every pin must pass through.
   *
   * Re-read whenever the attribute changes rather than cached at connect: the
   * embed sets it from an end-of-body script, and a pin batch can arrive at
   * any time afterwards.
   *
   * Absent attribute = not demo-gated = no restriction. Present but empty, or
   * unparseable, = gated with no resolved shop = pin NOTHING. Failing closed
   * matters more than usual here: showing a prospect every merchant's
   * locations is a data leak, showing them none is merely broken.
   *
   * @returns {Set<string> | null}
   */
  #demoAllowList() {
    const raw = this.getAttribute('data-demo-locations');
    if (raw === this.#demoAllowRaw) return this.#demoAllow;

    this.#demoAllowRaw = raw;
    if (raw == null) {
      this.#demoAllow = null;
      return null;
    }
    try {
      const parsed = JSON.parse(raw);
      this.#demoAllow = new Set(
        Array.isArray(parsed) ? parsed.filter((handle) => typeof handle === 'string' && handle) : []
      );
    } catch (error) {
      console.warn('Could not parse the demo location allow-list; pinning nothing:', error);
      this.#demoAllow = new Set();
    }
    return this.#demoAllow;
  }

  /** The pin filter in effect: the active search's matched handles, else the
   *  bar's idle set; null = no restriction (every pin shows). */
  #activePinFilter() {
    return this.#searchActive ? (this.#resultHandleSet ?? new Set()) : this.#idlePinHandles;
  }

  /** Whether a shop location's pin is visible under the filter in effect. */
  #isPinVisible(handle) {
    const filter = this.#activePinFilter();
    return !filter || filter.has(handle);
  }

  /**
   * Shows/hides the markers per the pin filter in effect: with a search active
   * only the matched locations keep their pin; with none, a collection page
   * keeps the locations carrying a product of the collection (the bar's idle
   * listing) and every other page keeps all pins. The viewport itself is the
   * bounding-box crop — a visible pin outside it simply isn't on screen.
   */
  #applyPinVisibility() {
    for (const [handle, location] of this.#locationsByHandle) {
      const element = location.marker?.getElement();
      if (element) element.style.display = this.#isPinVisible(handle) ? '' : 'none';
    }
  }

  /**
   * Shows a single shop location's card in the results pane — the pin-click
   * behavior while NO search is ongoing. There are no search results to page
   * through, so no product carousel opens; clicking the card navigates to the
   * shop location's page instead (see #buildLocationCard).
   *
   * @param {string} handle
   */
  #showLocationCard(handle) {
    const { searchResults, searchResultsBody } = this.refs;
    if (!searchResults || !searchResultsBody) return; // no pane (e.g. a slide-map)
    const location = this.#locationsByHandle.get(handle);
    if (!location) return;

    const list = document.createElement('ul');
    list.className = 'map-locations list-unstyled';
    list.appendChild(this.#buildLocationCard(location));
    searchResultsBody.replaceChildren(list);
    searchResults.classList.remove('is-loading');
    this.#openResults();
    this.#reflectActiveCard({ handle });
  }

  /**
   * Takes the empty-state copy off a `shopla:search:results` event. The bar
   * composes it — only it knows the search mode, and the same wording has to
   * serve its own fallback dropdown — and the map holds on to the latest,
   * because its pane can fall empty long after the event that carried it (a
   * pan that takes the last matched pin off screen).
   *
   * @param {{ emptyText?: unknown, register?: unknown }} detail
   */
  #noteEmptyState(detail) {
    if (typeof detail.emptyText === 'string') this.#emptyText = detail.emptyText;
    const register = detail.register;
    this.#register =
      register && typeof register === 'object' && typeof (/** @type any */ (register).url) === 'string'
        ? /** @type {{ prompt: string, cta: string, url: string }} */ (register)
        : null;
  }

  /**
   * Renders the empty-result state: the search matched nothing this map can
   * show. Uses the copy the bar sent with the results (see #noteEmptyState),
   * falling back to the generic "no products" label if a bar ever omits it.
   */
  #showPaneEmpty() {
    this.#showPaneNoResults(this.#emptyText || this.#labels.noResults, this.#register);
  }

  /**
   * Renders the empty-result card: a magnifier centred above the reason no
   * product matched (in bold), and — where the storefront configured a
   * registration link — the "Own a shop?" prompt as a paragraph under it.
   *
   * @param {string} text
   * @param {{ prompt: string, cta: string, url: string } | null} [register]
   */
  #showPaneNoResults(text, register = null) {
    const card = this.#buildPaneMessageCard();
    if (!card) return;
    card.classList.add('map-results-empty');
    const icon = document.createElement('span');
    icon.className = 'map-results-empty-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = SEARCH_ICON_SVG;
    card.appendChild(icon);
    const title = document.createElement('p');
    title.className = 'map-results-empty-title';
    title.textContent = text;
    card.appendChild(title);
    if (register) card.appendChild(buildRegisterPrompt(register, 'map-results-empty-register'));
  }

  /**
   * Renders a plain non-results message (error / hint) as a thin card the same
   * width as a shop-location card.
   *
   * @param {string} text
   */
  #showPaneMessage(text) {
    const card = this.#buildPaneMessageCard();
    if (card) card.textContent = text;
  }

  /**
   * Puts an empty message card in the pane and hands it back to be filled.
   * Null when there is no pane to put it in.
   *
   * @returns {HTMLElement | null}
   */
  #buildPaneMessageCard() {
    const { searchResultsBody } = this.refs;
    if (!searchResultsBody) return null;
    const wrap = document.createElement('div');
    wrap.className = 'map-results-message';
    const card = document.createElement('div');
    card.className = 'map-results-message-card';
    wrap.appendChild(card);
    searchResultsBody.replaceChildren(wrap);
    return card;
  }

  /**
   * Renders the demo-storefront "try it live" notice (title + body + optional
   * production link) in the results pane, in place of a shop-location list. The
   * search box hands over the content via a `demo` status event (see
   * map-search.js #showDemoNotice); the fallback dropdown renders a matching
   * card with the same class names.
   *
   * @param {{ title?: string, body?: string, url?: string, cta?: string }} demo
   */
  #showPaneDemoNotice(demo = {}) {
    const { searchResultsBody } = this.refs;
    if (!searchResultsBody) return;
    const wrap = document.createElement('div');
    wrap.className = 'map-results-message';
    const card = document.createElement('div');
    card.className = 'map-results-message-card map-results-demo-notice';
    if (demo.title) {
      const title = document.createElement('p');
      title.className = 'map-results-demo-notice-title';
      title.textContent = demo.title;
      card.appendChild(title);
    }
    if (demo.body) {
      const body = document.createElement('p');
      body.className = 'map-results-demo-notice-body';
      body.textContent = demo.body;
      card.appendChild(body);
    }
    if (demo.url && demo.cta) {
      const link = document.createElement('a');
      link.className = 'map-results-demo-notice-cta';
      link.href = demo.url;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = demo.cta;
      card.appendChild(link);
    }
    wrap.appendChild(card);
    searchResultsBody.replaceChildren(wrap);
  }

  /**
   * Renders the pane's loading state: a card-shaped skeleton the same size as a
   * shop-location card, sat where the first card would be (the top of the pane
   * on desktop, the card slot on the mobile sheet), with a spinner at its
   * centre. Shown while the result set — and so the shop count — is still
   * unknown, so the pane never flashes a small blank card that then grows into a
   * full-size result. Its wrapper deliberately isn't a `.map-locations` list, so
   * the first real render replaces it wholesale (see #renderLocationCards).
   */
  #showPaneSpinner() {
    const { searchResultsBody } = this.refs;
    if (!searchResultsBody) return;
    const wrap = document.createElement('div');
    wrap.className = 'map-results-loading';
    const card = document.createElement('div');
    card.className = 'map-location-card__skeleton';
    card.setAttribute('role', 'status');
    card.setAttribute('aria-label', this.#labels.searching);
    const spinner = document.createElement('span');
    spinner.className = 'map-spinner';
    spinner.innerHTML = SPINNER_SVG;
    card.appendChild(spinner);
    wrap.appendChild(card);
    searchResultsBody.replaceChildren(wrap);
  }

  /**
   * Lays out the result cards in the pane, reconciling against the cards
   * already shown: unchanged cards keep their existing DOM (so their images
   * aren't reloaded — no flicker), the distance tag and status line are
   * updated in place, new cards are built, and dropped ones are removed.
   *
   * The order is APPEND-ONLY for the life of a query+mode. The first render
   * lays the cards out RANKED: locations by first appearance in the product
   * results, and each "Shipping only" card at the position of its own
   * best-matching product — the bar's rank counts locations only, so each is
   * offset by the delivery cards already placed above it, and clamped (the
   * map drops matched locations it has no pin for, which can leave fewer
   * location cards than the bar counted). From then on every card keeps its
   * slot — a pin panning back into view returns to where it was — while
   * newcomers (results merged in by a pan, a first-seen pin) join at the
   * END, in that same ranked order among themselves. Re-ranking mid-search
   * would swap cards around under the visitor — and under the pane's scroll
   * position — for reasons only the ranking math knows; a new query or a
   * mode switch empties #displayOrder, so a deliberately new result set
   * ranks fresh.
   *
   * @param {{ handle: string, distance: number | null }[]} locations
   */
  #renderLocationCards(locations) {
    const { searchResultsBody } = this.refs;
    if (!searchResultsBody) return;

    // Reuse an existing list so a reload doesn't tear the pane down and rebuild.
    let list = searchResultsBody.querySelector('.map-locations');
    if (!list) {
      list = document.createElement('ul');
      list.className = 'map-locations list-unstyled';
      searchResultsBody.replaceChildren(list);
    }

    /** @type {Map<string, HTMLElement>} */
    const existing = new Map();
    /** @type {Map<string, HTMLElement>} */
    const existingDelivery = new Map();
    for (const card of list.querySelectorAll('.map-location-card')) {
      if (!(card instanceof HTMLElement)) continue;
      if (card.dataset.deliveryShop !== undefined) existingDelivery.set(card.dataset.deliveryShop, card);
      else if (card.dataset.handle) existing.set(card.dataset.handle, card);
    }

    // This render's candidates in RANKED order: the locations as given, each
    // delivery-only shop spliced in at its offset, clamped rank (see above).
    // The delivery cards are peers in the list even though local priority
    // still decides which carousel opens, so one can sit above a location
    // whose card is the active one.
    const candidates = locations.map(({ handle, distance }) => ({
      key: `loc:${handle}`,
      handle,
      delivery: false,
      distance: distance ?? null,
    }));
    const rankedShops = [...this.#deliveryShops].sort((a, b) => a.rank - b.rank);
    rankedShops.forEach(({ handle, rank }, placed) => {
      const at = Math.max(0, Math.min(rank + placed, candidates.length));
      candidates.splice(at, 0, { key: `del:${handle}`, handle, delivery: true, distance: null });
    });

    // Stable-append: cards this search has shown before keep their first-shown
    // order, newcomers register behind them — on the very first render that is
    // every card, i.e. the fully ranked layout.
    const order = this.#displayOrder;
    const known = [];
    const fresh = [];
    for (const candidate of candidates) (order.has(candidate.key) ? known : fresh).push(candidate);
    known.sort((a, b) => (order.get(a.key) ?? 0) - (order.get(b.key) ?? 0));

    const seen = new Set();
    for (const candidate of [...known, ...fresh]) {
      let card;
      if (candidate.delivery) {
        card = existingDelivery.get(candidate.handle);
        // A reused delivery card's "Ships to {region}" line may have resolved
        // (or changed) since it was built.
        if (card) this.#updateDeliveryShipsTo(card);
        else card = this.#buildDeliveryCard(candidate.handle);
      } else {
        card = existing.get(candidate.handle);
        if (card) {
          // Reuse the card DOM (no image reload); just refresh its distance tag
          // and its status line (opening hours ↔ "Ships to {region}" — the line
          // follows the search mode and the resolved shipping region).
          this.#updateLocationDistance(card, candidate.distance);
          this.#updateLocationStatusLine(card);
        } else {
          const location = this.#locationsByHandle.get(candidate.handle);
          if (!location) continue;
          card = this.#buildLocationCard(location, candidate.distance);
        }
      }
      list.appendChild(card); // moves an existing node into place — no reload
      seen.add(candidate.key);
      // The slot is claimed by actually rendering, so a candidate skipped
      // above can still take its first-shown place in a later render.
      if (!order.has(candidate.key)) order.set(candidate.key, this.#displaySeq++);
    }

    for (const [handle, card] of existing) {
      if (!seen.has(`loc:${handle}`)) card.remove();
    }
    for (const [handle, card] of existingDelivery) {
      if (!seen.has(`del:${handle}`)) card.remove();
    }

    this.#reflectActiveCard(this.#carouselState);
  }

  /** Updates (adds / refreshes / removes) a location card's distance tag in place. */
  #updateLocationDistance(card, distance) {
    const text = formatDistance(distance);
    let tag = card.querySelector('.map-location-card__distance');
    if (text) {
      if (!tag) {
        tag = document.createElement('span');
        tag.className = 'map-location-card__distance';
        card.querySelector('.map-location-card__button')?.appendChild(tag);
      }
      tag.textContent = text;
    } else if (tag) {
      tag.remove();
    }
  }

  /**
   * Builds one shop-location card (logo, cover, distance, title, address, status).
   *
   * @param {{ handle: string, meta: object }} location
   * @param {number | null} [distance] - Distance in meters (location searches).
   */
  #buildLocationCard(location, distance = null) {
    const meta = location.meta ?? {};
    const item = document.createElement('li');
    item.className = 'map-location-card';
    item.dataset.handle = location.handle;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'map-location-card__button';
    button.setAttribute('aria-label', meta.title ?? location.handle);

    // Cover — always rendered so the card layout is uniform and the logo badge
    // has a place to sit even when a location has no cover image.
    const media = document.createElement('div');
    media.className = 'map-location-card__cover';
    if (meta.cover) {
      const cover = document.createElement('img');
      cover.className = 'map-location-card__cover-img';
      cover.src = meta.cover;
      cover.alt = '';
      cover.loading = 'lazy';
      media.appendChild(cover);
    }
    button.appendChild(media);

    // Logo badge in the top-left corner, over the cover.
    if (meta.logo) {
      const logo = document.createElement('img');
      logo.className = 'map-location-card__logo';
      logo.width = CARD_LOGO_PX;
      logo.height = CARD_LOGO_PX;
      logo.src = sizedImageUrl(meta.logo, CARD_LOGO_PX * 2);
      logo.alt = '';
      logo.loading = 'lazy';
      button.appendChild(logo);
    }

    // Distance tag in the top-right corner, over the cover (location searches).
    const distanceText = formatDistance(distance);
    if (distanceText) {
      const tag = document.createElement('span');
      tag.className = 'map-location-card__distance';
      tag.textContent = distanceText;
      button.appendChild(tag);
    }

    const body = document.createElement('div');
    body.className = 'map-location-card__body';

    const text = document.createElement('div');
    text.className = 'map-location-card__text';

    const title = document.createElement('span');
    title.className = 'map-location-card__title';
    title.textContent = meta.title ?? '';
    text.appendChild(title);

    const address = this.#formatAddress(meta);
    if (address) {
      const addressEl = document.createElement('span');
      addressEl.className = 'map-location-card__address';
      addressEl.textContent = address;
      text.appendChild(addressEl);
    }

    const statusEl = this.#buildCardStatusLine(meta);
    if (statusEl) text.appendChild(statusEl);

    body.appendChild(text);
    button.appendChild(body);
    item.appendChild(button);

    button.addEventListener('click', () => {
      // During a search the card opens the location's product carousel; an
      // idle (pin-click) card navigates to the shop location's page instead —
      // there are no search results to browse.
      if (this.#searchActive) {
        this.#openCarousel(location.handle, { user: true });
      } else if (meta.url) {
        window.location.assign(meta.url);
      }
    });
    return item;
  }

  /**
   * The third line of a shop-location card, by what the current context is
   * about. A Shipping ("shipping") search shows "Ships to {region}" — a card
   * there means a matching product at the location ships to the visitor's
   * shipping region, so the door's opening hours aren't the point (and the
   * line is omitted entirely while no region is resolved yet). Everywhere else
   * — pickup searches and the idle pin-click card — the live opening-hours
   * status renders. The idle card deliberately ignores the bar's mode: with no
   * search there is no product match behind it, so nothing can be said about
   * where the location ships (shipping eligibility is a product property, not
   * a location one) — the hours are the only truthful line. Null when there is
   * nothing to show.
   *
   * @param {Record<string, any>} meta
   * @returns {HTMLElement | null}
   */
  #buildCardStatusLine(meta) {
    if (this.#searchActive && this.#searchMode === 'shipping') {
      if (!this.#shipsToRegion) return null;
      const shipsToEl = document.createElement('span');
      shipsToEl.className = 'map-location-card__ships-to';
      shipsToEl.textContent = template(this.#labels.shipsTo, { region: this.#shipsToRegion });
      return shipsToEl;
    }

    const status = computeOpenStatus(meta.openingHours, {
      // The place document's `country_code` — the timezone fallback for hours
      // that name none. Normalised to this name by BOTH feeds (#toLocations
      // here, #resolveLocations in map-search.js), so a card finds it whichever
      // one built it.
      countryCode: meta.countryCode,
      locale: this.#locale,
      labels: this.#labels,
    });
    if (!status) return null;
    const statusEl = document.createElement('span');
    statusEl.className = 'map-location-card__status ' + (status.open ? 'is-open' : 'is-closed');
    statusEl.textContent = status.label;
    return statusEl;
  }

  /**
   * Re-renders a reused location card's status line in place — the card DOM
   * survives re-renders (no image reload), but what its third line shows
   * depends on the search mode and the resolved shipping region, both of
   * which can change between renders (Pickup ↔ Shipping, a late resolution).
   *
   * @param {HTMLElement} card
   */
  #updateLocationStatusLine(card) {
    const meta = this.#locationsByHandle.get(card.dataset.handle ?? '')?.meta ?? {};
    const fresh = this.#buildCardStatusLine(meta);
    const current = card.querySelector('.map-location-card__status, .map-location-card__ships-to');
    if (current && fresh) current.replaceWith(fresh);
    else if (current) current.remove();
    else if (fresh) card.querySelector('.map-location-card__text')?.appendChild(fresh);
  }

  /**
   * Builds one shop's "Shipping only" card — the stand-in for that shop's
   * delivery-only products, which have no location to pin.
   *
   * Rendered as a shop-location card in every respect that a shop has: same
   * classes (so the pane's layout, hover and active styling apply unchanged),
   * the shop collection's cover and logo, and its name as the title. The
   * "Shipping only" badge takes the slot the opening-hours status occupies on
   * a location card. Address, distance and opening hours are omitted — they
   * belong to a location, and a shop has none.
   *
   * Falls back to an unbranded card (a van icon and a generic title) when the
   * shop has no branding to render from: the shop isn't in the places index
   * yet, the lookup failed, or the search service predates `shop_handle`, in
   * which case every delivery-only hit lands in one bucket keyed "".
   *
   * @param {string} shopHandle
   */
  #buildDeliveryCard(shopHandle) {
    const shop = this.#shopsByHandle.get(shopHandle);

    const item = document.createElement('li');
    item.className = 'map-location-card map-location-card--delivery';
    // Marks it for the reconcile pass and for #reflectActiveCard; deliberately
    // not data-handle, which would collide with a real shop location.
    item.dataset.deliveryShop = shopHandle;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'map-location-card__button';
    button.setAttribute('aria-label', shop?.title || this.#labels.deliveryOnlyTitle);

    // Cover — always rendered so the card matches a location card's layout even
    // when the shop has no image, exactly as #buildLocationCard does.
    const media = document.createElement('div');
    media.className = 'map-location-card__cover';
    if (shop?.cover) {
      const cover = document.createElement('img');
      cover.className = 'map-location-card__cover-img';
      cover.src = shop.cover;
      cover.alt = '';
      cover.loading = 'lazy';
      media.appendChild(cover);
    } else if (!shop) {
      // No shop to brand with: the van icon stands in for the missing cover.
      media.classList.add('map-location-card__cover--delivery');
      media.innerHTML = DELIVERY_ICON;
    }
    button.appendChild(media);

    if (shop?.logo) {
      const logo = document.createElement('img');
      logo.className = 'map-location-card__logo';
      logo.width = CARD_LOGO_PX;
      logo.height = CARD_LOGO_PX;
      logo.src = sizedImageUrl(shop.logo, CARD_LOGO_PX * 2);
      logo.alt = '';
      logo.loading = 'lazy';
      button.appendChild(logo);
    }

    const body = document.createElement('div');
    body.className = 'map-location-card__body';

    const text = document.createElement('div');
    text.className = 'map-location-card__text';

    const title = document.createElement('span');
    title.className = 'map-location-card__title';
    title.textContent = shop?.title || this.#labels.deliveryOnlyTitle;
    text.appendChild(title);

    // One row where a location card shows its open/closed status: the
    // "Ships to {region}" line (the same line the shop-location cards show in
    // delivery mode, since this card exists only there) with the "Shipping
    // only" badge BESIDE it. The badge keeps the wording the shop-grouped
    // collection template uses, so the two surfaces name the concept
    // identically; the ships-to half is omitted while no shipping region is
    // resolved (a late resolution reaches it via #updateDeliveryShipsTo).
    const row = document.createElement('span');
    row.className = 'map-location-card__ships-row';
    if (this.#shipsToRegion) {
      const shipsTo = document.createElement('span');
      shipsTo.className = 'map-location-card__ships-to';
      shipsTo.textContent = template(this.#labels.shipsTo, { region: this.#shipsToRegion });
      row.appendChild(shipsTo);
    }
    const badge = document.createElement('span');
    badge.className = 'map-location-card__badge';
    badge.textContent = this.#labels.deliveryOnlyBadge;
    row.appendChild(badge);
    text.appendChild(row);

    body.appendChild(text);
    button.appendChild(body);
    item.appendChild(button);

    button.addEventListener('click', () => {
      if (this.#searchActive) this.#openDeliveryCarousel(shopHandle, { user: true });
    });
    return item;
  }

  /**
   * Re-renders a reused delivery card's "Ships to {region}" line in place —
   * added, refreshed or removed as the resolved shipping region changes
   * between renders, always kept beside (before) the "Shipping only" badge
   * in the shared row.
   *
   * @param {HTMLElement} card
   */
  #updateDeliveryShipsTo(card) {
    const row = card.querySelector('.map-location-card__ships-row');
    if (!row) return;

    let line = row.querySelector('.map-location-card__ships-to');
    if (!this.#shipsToRegion) {
      line?.remove();
      return;
    }
    if (!line) {
      line = document.createElement('span');
      line.className = 'map-location-card__ships-to';
      row.insertBefore(line, row.querySelector('.map-location-card__badge'));
    }
    line.textContent = template(this.#labels.shipsTo, { region: this.#shipsToRegion });
  }

  /** "address1, zip city, country" for a location, dropping blank parts. */
  #formatAddress(meta) {
    const cityLine = [meta.zip, meta.city].map((part) => (part ?? '').trim()).filter(Boolean).join(' ');
    return [meta.address1, cityLine, meta.country]
      .map((part) => (part ?? '').trim())
      .filter(Boolean)
      .join(', ');
  }

  /* ────────────────────────────────────────────────── product carousel (map) */

  /**
   * Opens (or moves) the floating product carousel above a shop location's pin
   * and loads its first page of products. If the pin is off-screen the map
   * eases to it; if it's already visible the map stays put (so typing doesn't
   * jitter the map as results refresh). A pan-driven hand-over (see
   * #syncCarouselToBestVisible) passes `center: false` — the map must never
   * move under the visitor's own pan, even if the carousel ends up near an edge.
   *
   * A visitor's own click on a card or pin passes `user: true`: the selection
   * then holds against the best-visible re-sync that the recentering (and the
   * results its bounds change loads) would otherwise trigger — see
   * #syncCarouselToBestVisible. Programmatic opens (auto-open of the best
   * match, pan-driven hand-overs) leave it unset.
   *
   * @param {string} handle
   * @param {{ center?: boolean, user?: boolean }} [options]
   */
  #openCarousel(handle, { center = true, user = false } = {}) {
    const location = this.#locationsByHandle.get(handle);
    if (!location || !this.#activeSearch || !this.#map) return;

    this.#ensureCarouselElement();

    const state = { kind: 'location', handle, userSelected: user, query: this.#currentQuery, mode: this.#searchMode, pages: [], page: 0, hasMore: true, loading: false, superseded: false, loadSeq: 0 };
    this.#carouselState = state;
    this.#reopenBestOnPan = false;

    // The shared carousel element can only be in one host at a time; take it
    // back from the centred host in case a delivery carousel was open.
    this.#unmountCentredCarousel();
    this.#carousel.setLngLat(location.coords).setDOMContent(this.#carouselEl).addTo(this.#map);
    this.#reflectActiveCard(state);

    // Bring the pin into view when it isn't comfortably on screen. A pin merely
    // *inside* the bounds can still sit near an edge, where the carousel (which
    // is centered on the pin and extends up/out) would be clipped by the map's
    // overflow — so keep clear of the edges, not just fully off-screen.
    if (center && !this.#pinComfortablyVisible(location.coords)) {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      this.#map.easeTo({ center: location.coords, offset: mobile ? [0, 40] : [120, 60], duration: 300, essential: true });
    }

    // Go straight to loading page 0 — it renders the loading spinner itself. A
    // separate pre-render here would run with `loading` still false and briefly
    // paint the "No products" empty message before the fetch even starts.
    this.#goToCarouselPage(0);
  }

  /**
   * Opens the DELIVERY-ONLY carousel: the same carousel, floated in the middle
   * of the map instead of over a pin, because these products have no shop
   * location to point at. The map never moves for it — there is nothing to
   * centre on — and no pin is marked active.
   *
   * Reached two ways: automatically, when a delivery search turns up nothing
   * local inside the map's box (see #bestLocatedHandle) — the top-ranked shop
   * opens — and on demand, when the visitor clicks a "Shipping only" card.
   *
   * @param {string} shopHandle - Which shop's delivery-only products to show.
   * @param {{ user?: boolean }} [options] - `user: true` marks a visitor's own
   *   card click, which the best-visible re-sync then respects (see
   *   #syncCarouselToBestVisible).
   */
  #openDeliveryCarousel(shopHandle, { user = false } = {}) {
    if (!this.#activeSearch || !this.#map) return;
    if (!this.#deliveryShops.some((shop) => shop.handle === shopHandle)) return;
    // An older bar (no delivery support) can still be the one that ran the
    // search — don't open a carousel it cannot fill.
    if (typeof this.#activeSearch.loadDeliveryProducts !== 'function') return;

    this.#ensureCarouselElement();

    const state = { kind: 'delivery', handle: null, shopHandle, userSelected: user, query: this.#currentQuery, mode: this.#searchMode, pages: [], page: 0, hasMore: true, loading: false, superseded: false, loadSeq: 0 };
    this.#carouselState = state;
    this.#reopenBestOnPan = false;

    // Hand the shared element to the centred host, taking it off the pin popup.
    this.#carousel?.remove();
    this.#mountCentredCarousel();
    this.#reflectActiveCard(state);

    this.#goToCarouselPage(0);
  }

  /**
   * Moves the shared carousel element into a centred overlay inside the map.
   * Deliberately not a Mapbox popup pinned to the map centre: a popup is
   * anchored to a coordinate, so it would drift off-centre as soon as the
   * visitor pans, and it would draw a tip pointing at empty ground.
   */
  #mountCentredCarousel() {
    if (!this.#centredCarouselHost) {
      const host = document.createElement('div');
      host.className = 'shopla-carousel-centred';
      this.appendChild(host);
      this.#centredCarouselHost = host;
    }
    this.#centredCarouselHost.appendChild(this.#carouselEl);
    this.#centredCarouselHost.hidden = false;
  }

  /** Empties and hides the centred host (a no-op when it was never built). */
  #unmountCentredCarousel() {
    const host = this.#centredCarouselHost;
    if (!host) return;
    if (this.#carouselEl?.parentNode === host) host.removeChild(this.#carouselEl);
    host.hidden = true;
  }

  /**
   * Refreshes the open carousel's products in place after a query change (or
   * after its page load was superseded): reloads page 0 for the current query
   * while the current cards stay on screen, then swaps them — no loading
   * placeholder, so it doesn't blink; a carousel still on its spinner keeps
   * spinning. Falls back to a full open if the carousel isn't the one passed in.
   *
   * @param {{ kind: string, handle: string | null }} target - The open carousel.
   */
  #refreshCarousel(target) {
    const state = this.#carouselState;
    if (!state || state !== target) {
      if (target?.kind === 'delivery') this.#openDeliveryCarousel(target.shopHandle, { user: target.userSelected === true });
      else if (target?.handle) this.#openCarousel(target.handle, { user: target.userSelected === true });
      return;
    }

    state.query = this.#currentQuery;
    state.mode = this.#searchMode;
    state.superseded = false;
    // A refresh re-asks the question, so last answer's failure is history: left
    // set, a stale 'error' would make the NEXT genuinely-empty query say
    // "couldn't load".
    state.blankReason = null;
    // Loading — but without a render: the current cards stay up (no blink),
    // and where nothing is loaded yet the spinner stays. The flag also holds
    // page loads and prefetches off until this settles: the bar runs one
    // carousel load at a time, so either would abort this one.
    state.loading = true;
    const seq = ++state.loadSeq;

    /**
     * @param {{ products?: { id: string, html: string }[], hasMore?: boolean }} result
     * @param {boolean} [failed] - The load failed rather than came back empty.
     */
    const settle = (result, failed = false) => {
      // Superseded (carousel closed / switched location / newer load)?
      if (this.#carouselState !== state || state.loadSeq !== seq) return;

      const products = result.products ?? [];
      // Reconcile page 0 against the cards on screen: reuse a card's DOM when
      // its product persists (no image reload → no blink), build only the new
      // ones, in the new order — like the shop-location cards.
      const existing = new Map((state.pages[0] ?? []).map((node) => [this.#productIdOf(node), node]));
      const nodes = products
        .map((product) => existing.get(product.id) || this.#buildProductCard(product.html))
        .filter(Boolean);
      state.pages = nodes.length ? [nodes] : [];
      state.page = 0;
      state.hasMore = Boolean(result.hasMore);
      state.loading = false;
      // Why the carousel is blank, for the message #renderCarousel picks. Set
      // before the drop below, which is the other half of the same rule: a
      // blank that FAILED gets the "couldn't load" message and keeps its card,
      // while a blank that genuinely answered has no card left to put a
      // message on.
      state.blankReason = failed ? 'error' : null;
      // Same invariant as a page load: a refined query that leaves this shop
      // with nothing renderable leaves a card standing over an empty carousel,
      // so the card goes too (see #dropEmptyResult). Only when the load
      // actually answered — a failure says nothing about the shop.
      if (!failed && nodes.length === 0 && this.#dropEmptyResult(state)) return;
      this.#renderCarousel();
    };
    Promise.resolve(this.#loadCarouselPage(state, 0)).then(
      (result) => settle(result),
      (error) => {
        // Superseded in the bar while this carousel still wants the page: hold
        // it as it is for the results event that follows (see isSuperseded).
        // Any other failure settles as an empty page — that IS the answer, but
        // it is a FAILED one, so the blank says so.
        if (isSuperseded(error)) {
          if (this.#carouselState === state && state.loadSeq === seq) state.superseded = true;
          return;
        }
        console.warn('map-search: carousel refresh failed to load —', error);
        settle({ products: [], hasMore: false }, true);
      }
    );
  }

  /** The product id a carousel card carries (from its resource-card), or "". */
  #productIdOf(node) {
    return node.querySelector('[data-product-id]')?.getAttribute('data-product-id') ?? '';
  }

  /**
   * Whether a pin sits far enough from every edge of the map container that its
   * (pin-centered, upward-extending) carousel won't be clipped by the map's
   * overflow. Uses pixel projection so it accounts for near-edge pins, not just
   * off-screen ones.
   *
   * @param {[number, number]} coords
   */
  #pinComfortablyVisible(coords) {
    const container = this.#map?.getContainer();
    if (!container) return true;
    let point;
    try {
      point = this.#map.project(coords);
    } catch {
      return true;
    }
    const width = container.clientWidth;
    const height = container.clientHeight;
    // Keep-clear margins: the carousel is roughly ±165px wide and reaches ~240px
    // above the pin (its cards plus the close button that sits above them), with
    // little below it.
    const marginX = 170;
    const marginTop = 240;
    const marginBottom = 110;
    return point.x >= marginX && point.x <= width - marginX && point.y >= marginTop && point.y <= height - marginBottom;
  }

  /** Closes the carousel (leaving the pane untouched). Every close path —
   *  the close button, an outside press, a map click, empty/error results —
   *  drops a pending pan reopen; only #syncCarouselToBestVisible re-arms it
   *  (a pan-out with nothing matched in view). */
  #closeCarousel() {
    this.#reopenBestOnPan = false;
    if (!this.#carouselState) return;
    this.#carouselState = null;
    this.#carousel?.remove();
    this.#unmountCentredCarousel();
    this.#reflectActiveCard(null);
  }

  /** Builds the reusable carousel shell (arrows + track + close) once. */
  #ensureCarouselElement() {
    if (this.#carouselEl) return;

    const mapboxgl = this.#mapboxgl;
    this.#carousel = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      anchor: 'bottom',
      offset: [0, -48],
      maxWidth: 'none',
      className: 'shopla-carousel-popup',
      // Don't steal focus from the search input when the carousel opens/moves
      // as the visitor types (Mapbox focuses the popup by default).
      focusAfterOpen: false,
    });

    const root = document.createElement('div');
    root.className = 'shopla-carousel';
    // The carousel lives in a map popup, outside the .map-results pane that
    // carries --card-corner-radius, so pass the store's radius through here.
    const radius = this.dataset.cardCornerRadius;
    if (radius) root.style.setProperty('--card-corner-radius', `${radius}px`);

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'shopla-carousel__close';
    close.setAttribute('aria-label', this.#labels.close);
    close.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
    close.addEventListener('click', () => this.#closeCarousel());

    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'shopla-carousel__nav shopla-carousel__nav--prev';
    prev.setAttribute('aria-label', this.#labels.prev);
    prev.innerHTML = CHEVRON_LEFT;
    prev.addEventListener('click', () => this.#goToCarouselPage((this.#carouselState?.page ?? 0) - 1));

    const track = document.createElement('ul');
    track.className = 'shopla-carousel__track list-unstyled';

    // The cards' frame: the close button hangs off its top-right corner — the
    // cards', not the whole strip's — so it stays beside the cards whether or
    // not the "next" chevron is shown (see .shopla-carousel__frame in
    // map-search.css).
    const frame = document.createElement('div');
    frame.className = 'shopla-carousel__frame';

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'shopla-carousel__nav shopla-carousel__nav--next';
    next.setAttribute('aria-label', this.#labels.next);
    next.innerHTML = CHEVRON_RIGHT;
    next.addEventListener('click', () => this.#goToCarouselPage((this.#carouselState?.page ?? 0) + 1));

    frame.append(track, close);
    root.append(prev, frame, next);
    this.#carouselEl = root;
    this.#carouselRefs = { track, prev, next };

    this.#enableCarouselSwipe(track);
  }

  /**
   * Makes the carousel swipeable by touch/pen: a horizontal drag past a small
   * threshold pages the carousel (swipe left → next, right → previous), reusing
   * the same paged navigation (and slide animation) as the chevrons. Vertical
   * drags are ignored; the underlying map never pans (events are stopped), and
   * the click that follows a swipe is suppressed so it doesn't open a product.
   *
   * @param {HTMLElement} surface - The carousel track (the cards area).
   */
  #enableCarouselSwipe(surface) {
    const THRESHOLD = 40; // px of horizontal travel that counts as a swipe
    let startX = 0;
    let startY = 0;
    let pointerId = null;
    let axis = null; // 'x' | 'y' | null (undecided)
    let swiped = false;

    surface.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse') return; // desktop uses the chevrons
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      axis = null;
      swiped = false;
      // Keep the map from starting a drag-pan under the carousel.
      event.stopPropagation();
    });

    surface.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      if (axis === null && Math.abs(dx) + Math.abs(dy) > 8) {
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (axis === 'x') {
        event.stopPropagation();
        if (event.cancelable) event.preventDefault();
      }
    });

    const finish = (event) => {
      if (event.pointerId !== pointerId) return;
      const dx = event.clientX - startX;
      if (axis === 'x' && Math.abs(dx) >= THRESHOLD) {
        swiped = true; // suppress the trailing click on a card
        const page = this.#carouselState?.page ?? 0;
        this.#goToCarouselPage(dx < 0 ? page + 1 : page - 1);
      }
      pointerId = null;
      axis = null;
    };
    surface.addEventListener('pointerup', finish);
    surface.addEventListener('pointercancel', () => {
      pointerId = null;
      axis = null;
    });

    // A swipe ends on a card, which would otherwise fire a click → product page.
    surface.addEventListener(
      'click',
      (event) => {
        if (!swiped) return;
        swiped = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  }

  /**
   * Fetches one page of the open carousel's products from the bar, picking the
   * loader that matches the carousel's kind: a location carousel pulls a
   * `coll`-scoped page for its shop location, a delivery carousel pulls from
   * the search's blank-handle results. Same `{ products, hasMore }` contract
   * either way, so every caller downstream is kind-agnostic.
   *
   * @param {{ kind: string, handle: string | null }} state
   * @param {number} page - Page index.
   */
  #loadCarouselPage(state, page) {
    const from = page * CAROUSEL_PAGE;
    return state.kind === 'delivery'
      ? this.#activeSearch.loadDeliveryProducts(state.shopHandle, from, CAROUSEL_PAGE)
      : this.#activeSearch.loadLocationProducts(state.handle, from, CAROUSEL_PAGE);
  }

  /**
   * Shows a carousel page, fetching it (via the bar) when not already loaded.
   *
   * @param {number} target - The page index to show.
   * @param {number} [skips] - How many all-unrenderable pages have been skipped
   *   in this forward run (bounded by CAROUSEL_MAX_EMPTY_SKIPS).
   */
  async #goToCarouselPage(target, skips = 0) {
    const state = this.#carouselState;
    if (!state || target < 0) return;
    const direction = target > state.page ? 'next' : target < state.page ? 'prev' : null;

    if (state.pages[target]) {
      state.page = target;
      this.#renderCarousel(direction);
      return;
    }
    if (!state.hasMore || state.loading) return;

    const seq = ++state.loadSeq;
    state.loading = true;
    this.#renderCarousel(); // loading state — no slide

    let result;
    // Whether the page came back empty because the load FAILED, as opposed to
    // there being nothing. Both settle as an empty page, but only the second
    // says anything about the shop. It decides two things: what the blank says
    // (`blankReason` below), and whether the card may be dropped at all — which
    // must never happen over a transient error.
    let failed = false;
    try {
      result = await this.#loadCarouselPage(state, target);
    } catch (error) {
      // Superseded in the bar while this carousel still wants the page (a
      // keystroke, mode or location change aborted it): leave it loading —
      // the spinner, or the current page with its arrows disabled, stays up —
      // for the results event that follows, which refreshes or replaces it
      // (see #settleCarousel). An empty page here would paint "No products
      // found" before the answer is known.
      if (isSuperseded(error)) {
        if (this.#carouselState === state && state.loadSeq === seq) state.superseded = true;
        return;
      }
      // Any other failure ends the paging — but it is not an EMPTY page. The
      // page stays blank either way; `failed` is what lets the blank say
      // "couldn't load" rather than "this shop has none", and what keeps the
      // card from being dropped over it.
      console.warn('map-search: carousel page failed to load —', error);
      result = { products: [], hasMore: false };
      failed = true;
    }

    // Superseded by a newer load (carousel closed / switched location / newer page)?
    if (this.#carouselState !== state || state.loadSeq !== seq) return;

    state.loading = false;
    const nodes = (result.products ?? []).map((product) => this.#buildProductCard(product.html)).filter(Boolean);
    if (nodes.length === 0) {
      // No renderable cards on this page. `hasMore` reflects whether the service
      // still has *raw* matches beyond it — a page whose products are all
      // unavailable in the storefront renders nothing yet isn't the end. Skip
      // forward in that case (bounded); only stop when the service is truly out.
      state.hasMore = Boolean(result.hasMore);
      // Why the page is blank decides what the visitor should be told. A
      // request that FAILED is "something went wrong", not "this shop has
      // none". A page whose cards simply would not render is skipped over
      // above, so it never reaches the message.
      state.blankReason = failed ? 'error' : null;
      if (state.hasMore && skips < CAROUSEL_MAX_EMPTY_SKIPS) {
        this.#goToCarouselPage(target + 1, skips + 1);
        return;
      }
      // Out of pages with NOTHING ever shown (`pages` is still untouched): this
      // result has no product the storefront can render, so its card claims a
      // shop that has nothing to offer for this search. Drop it rather than
      // render the empty carousel that has been reported three times now —
      // whatever the next cause turns out to be, a card and its carousel can no
      // longer disagree. A FAILED load is excluded: it says nothing about the
      // shop, and deleting a card over a dropped request would be worse than
      // the empty carousel.
      if (!failed && state.pages.length === 0 && this.#dropEmptyResult(state)) return;
      this.#renderCarousel();
      return;
    }
    state.pages[target] = nodes;
    state.hasMore = Boolean(result.hasMore);
    state.blankReason = null;
    state.page = target;
    this.#renderCarousel(direction);
  }

  /**
   * Removes a result whose carousel turned out to have nothing to show at all —
   * its card leaves the results pane and, for a shop location, its pin leaves
   * the map — then opens the next-best result in its place.
   *
   * This is the structural half of the empty-carousel fix. The other half
   * removes the cause (map-search.js now renders cards by fetching each product
   * on its own route instead of asking Shopify's predictive search for it, so a
   * live product can no longer come back unrenderable). This half makes the
   * SYMPTOM impossible regardless of cause: a card is only ever shown for a
   * shop whose products the storefront could actually render.
   *
   * It is self-healing rather than predictive — nothing can know a carousel is
   * empty until it has been loaded — and it cascades: opening the next best
   * discovers and drops that one too if it is empty as well, until a result
   * with products is found or the pane reports that nothing matched.
   *
   * @param {{ kind: string, handle: string, shopHandle?: string }} state - The
   *   carousel that came back empty.
   * @returns {boolean} Whether anything was dropped (false when the result had
   *   already left the set, e.g. a newer search replaced it).
   */
  #dropEmptyResult(state) {
    if (state.kind === 'delivery') {
      const kept = this.#deliveryShops.filter((shop) => shop.handle !== state.shopHandle);
      if (kept.length === this.#deliveryShops.length) return false;
      this.#deliveryShops = kept;
      console.warn(
        `map-search: shop "${state.shopHandle}" matched the search but has no product the storefront can render — its card was removed`
      );
    } else {
      if (!this.#resultHandles.includes(state.handle)) return false;
      this.#resultHandles = this.#resultHandles.filter((handle) => handle !== state.handle);
      this.#resultHandleSet = new Set(this.#resultHandles);
      this.#resultLocations = this.#resultLocations.filter((location) => location.handle !== state.handle);
      // Let it start over at the end of the pane's append-only order if a later
      // search brings it back with products — its old stamp is meaningless now.
      this.#displayOrder.delete(state.handle);
      console.warn(
        `map-search: location "${state.handle}" matched the search but has no product the storefront can render — its card and pin were removed`
      );
    }

    this.#closeCarousel();
    this.#applyPinVisibility();

    const empty = this.#resultHandles.length === 0 && this.#deliveryShops.length === 0;
    if (empty) this.#resultLocations = [];

    // A slide-map has no pane; only its pins and the carousel exist there.
    if (this.refs.searchResults && this.refs.searchResultsBody) {
      if (empty) this.#showPaneEmpty();
      else this.#renderVisibleLocations();
    }
    if (empty) return true;

    // Same choice a fresh result set makes (see #handleSearchResults), minus
    // the recentering: dropping a card is not a reason to move the map under
    // the visitor.
    const bestLocated = this.#bestLocatedHandle();
    if (bestLocated) this.#openCarousel(bestLocated, { center: false });
    else if (this.#deliveryShops.length > 0) this.#openDeliveryCarousel(this.#deliveryShops[0].handle);
    else if (this.#resultHandles.length > 0) this.#openCarousel(this.#resultHandles[0], { center: false });
    return true;
  }

  /**
   * Reflects the current carousel state into the shell (cards + arrow states).
   *
   * @param {'next' | 'prev' | null} [direction] - When set, the freshly-shown
   *   page slides in from that direction.
   */
  #renderCarousel(direction = null) {
    const state = this.#carouselState;
    if (!state || !this.#carouselRefs) return;
    const { track, prev, next } = this.#carouselRefs;

    const nodes = state.pages[state.page] ?? [];
    if (nodes.length > 0) {
      // The page's card nodes persist across renders (cached in state.pages), so
      // re-inserting them never reloads their images — no blink, even when the
      // same location's results refresh (see #refreshCarousel's reconciliation).
      track.replaceChildren(...nodes);
      if (direction) this.#animateCarousel(track, direction);
    } else if (state.loading) {
      // Still fetching — show a spinner where the first product card would be
      // (a card-sized placeholder), never the "No products" message: the count
      // isn't known yet, so an empty message here would be premature.
      const loading = document.createElement('li');
      loading.className = 'shopla-carousel__message shopla-carousel__message--loading';
      loading.setAttribute('role', 'status');
      loading.setAttribute('aria-label', this.#labels.searching);
      const spinner = document.createElement('span');
      spinner.className = 'map-spinner';
      spinner.innerHTML = SPINNER_SVG;
      loading.appendChild(spinner);
      track.replaceChildren(loading);
    } else {
      // The load finished with nothing to show. WHY decides what to say: only a
      // completed search that genuinely matched nothing is "no products found".
      // A failed request, or matches whose cards could not be rendered, are
      // errors — reporting them as an empty shop is how this stayed invisible.
      const message = document.createElement('li');
      message.className = 'shopla-carousel__message';
      if (state.blankReason) message.classList.add('shopla-carousel__message--error');
      message.textContent = state.blankReason ? this.#labels.loadFailed : this.#labels.noResults;
      track.replaceChildren(message);
    }

    // Hide (not just disable) a chevron when there's nothing that way.
    prev.hidden = state.page === 0;
    next.hidden = !this.#hasNextPage(state);
    prev.disabled = state.loading;
    next.disabled = state.loading;

    // Eagerly fetch the next page so tapping "next" is instant.
    this.#prefetchNextPage();
  }

  /** Whether there is (or may be) a page after the current one. */
  #hasNextPage(state) {
    return Boolean(state.pages[state.page + 1]) || state.hasMore;
  }

  /** Slides the freshly-rendered page in from the direction of travel. */
  #animateCarousel(track, direction) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const dx = direction === 'next' ? 28 : -28;
    track.animate(
      [
        { transform: `translateX(${dx}px)`, opacity: 0.2 },
        { transform: 'translateX(0)', opacity: 1 },
      ],
      { duration: 220, easing: 'ease-out' }
    );
  }

  /**
   * Passively prefetches the next page's products (no spinner, no page change)
   * so tapping "next" resolves instantly. A no-op once it's cached / at the end.
   */
  #prefetchNextPage() {
    const state = this.#carouselState;
    if (!state || !this.#activeSearch) return;
    const target = state.page + 1;
    if (state.pages[target] || !state.hasMore || state.loading) return;

    const seq = state.loadSeq; // passive — don't supersede an active load

    /** @param {{ products?: { html: string }[], hasMore?: boolean }} result */
    const settle = (result) => {
      // Carousel closed / switched location, or the visitor already navigated?
      if (this.#carouselState !== state || state.loadSeq !== seq) return;
      const nodes = (result.products ?? []).map((product) => this.#buildProductCard(product.html)).filter(Boolean);
      if (nodes.length) state.pages[target] = nodes;
      state.hasMore = Boolean(result.hasMore);
      // Reveal the "next" chevron now that the page is known (no re-animation).
      const next = this.#carouselRefs?.next;
      if (next) next.hidden = !this.#hasNextPage(state);
    };
    // Through the loader that matches the carousel's kind: a delivery carousel
    // pages its shop's delivery-only results, not a location's.
    Promise.resolve(this.#loadCarouselPage(state, target)).then(settle, (error) => {
      // Superseded: nothing to record. The chevron stands as page N left it
      // and a tap loads the page on demand; a results event that refreshes
      // the carousel prefetches again. Any other failure ends the paging.
      if (!isSuperseded(error)) settle({ products: [], hasMore: false });
    });
  }

  /** Parses a product card's `<li>` HTML (from the bar) into a node, or null.
   *  Parsed inside a `<ul>` so the `<li>` is retained as-is. */
  #buildProductCard(html) {
    const list = document.createElement('ul');
    list.innerHTML = html ?? '';
    const node = list.firstElementChild;
    return node instanceof HTMLElement ? node : null;
  }

  /**
   * Marks the card (and pin) the open carousel belongs to, clearing the others.
   *
   * @param {{ kind?: string, handle?: string, shopHandle?: string } | null} state
   *   - The carousel state, or `null` when no carousel is open. A delivery
   *   carousel activates its shop's "Shipping only" card and no pin — it has no
   *   location to mark.
   */
  #reflectActiveCard(state) {
    const delivery = state?.kind === 'delivery';
    const handle = state && !delivery ? state.handle : null;
    const shopHandle = delivery ? state.shopHandle : null;

    const { searchResultsBody } = this.refs;
    if (searchResultsBody) {
      for (const card of searchResultsBody.querySelectorAll('.map-location-card')) {
        if (!(card instanceof HTMLElement)) continue;
        const active =
          card.dataset.deliveryShop !== undefined
            ? delivery && card.dataset.deliveryShop === shopHandle
            : card.dataset.handle === handle;
        card.classList.toggle('is-active', active);
      }
    }
    for (const [key, location] of this.#locationsByHandle) {
      location.marker?.getElement()?.classList.toggle('shopla-marker-active', key === handle);
    }
  }

  /**
   * Closes the context menu and/or the carousel when a click/tap lands
   * outside the map.
   *
   * @param {Event} event
   */
  #handleDocumentPointerDown = (event) => {
    const target = event.target;
    if (!(target instanceof Node)) return;

    // The menu closes on any press outside itself — including the right-button
    // press of the NEXT right-click, whose contextmenu event then reopens it
    // at the new point.
    if (this.#contextMenu && !this.#contextMenu.hidden && !this.#contextMenu.contains(target)) {
      this.#closeContextMenu();
    }

    if (!this.#carouselState) return;
    // Presses inside the map never close the carousel from here: the press
    // that starts a pan is not a dismissal (pin visibility is re-synced on
    // moveend instead), and a genuine in-map click closes it via the map's
    // own 'click' event — which a drag never fires. That also covers the
    // location cards, pins and the carousel itself, which all live inside
    // the map element.
    if (this.contains(target)) return;
    this.#closeCarousel();
  };

  /* ─────────────────────────────────────────────────────────── pane chrome */

  /**
   * Records the query behind an incoming search event and, when it differs from
   * the last one seen, clears the merchant's manual-close state — a close only
   * holds for the query it was made on, so a new query lets the pane auto-open
   * again. Ignores events without a query (older bars).
   *
   * @param {string} [query]
   */
  #noteQuery(query) {
    if (typeof query !== 'string' || query === this.#currentQuery) return;
    this.#currentQuery = query;
    this.#resultsUserClosed = false;
    // A new query is a deliberately new result set — its pane ranks fresh
    // rather than being held to the old query's append-only order.
    this.#displayOrder.clear();
  }

  /** Slides the results pane in and hides the reopen toggle. */
  #openResults() {
    const { searchResults, searchResultsToggle } = this.refs;
    // `.is-open` drives the slide-in transition (see assets/map-search.css).
    if (searchResults) searchResults.classList.add('is-open');
    if (searchResultsToggle) searchResultsToggle.hidden = true;
  }

  /** Slides the pane out (close button) but keeps a toggle to reopen it. The
   *  merchant closed it deliberately, so it stays closed for this query — no
   *  incoming result (a pan, a re-emit) reopens it; only the toggle, or a new
   *  query, does. */
  #collapseResults = () => {
    const { searchResults, searchResultsToggle } = this.refs;
    if (searchResults) searchResults.classList.remove('is-open');
    if (searchResultsToggle) searchResultsToggle.hidden = false;
    this.#resultsUserClosed = true;
  };

  /** Reopens the collapsed results pane (the toggle). An explicit reopen clears
   *  the merchant-closed state, so it stays open as this query's results
   *  update. */
  #expandResults = () => {
    this.#resultsUserClosed = false;
    this.#openResults();
  };

  /**
   * Reflects the browser's fullscreen state on the element so the
   * fullscreen-only search bar (and pane sizing) can respond via CSS.
   */
  #syncFullscreen = () => {
    const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement || null;
    this.toggleAttribute('data-fullscreen', fullscreenElement === this);
  };

  /**
   * Sets --keyboard-inset to how far the on-screen keyboard overlaps the map's
   * bottom edge, so the mobile results pane can sit just above the keyboard
   * while typing (0 when no keyboard). The visual viewport shrinks when the
   * keyboard opens; its bottom in layout coordinates is offsetTop + height.
   */
  #updateKeyboardInset = () => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const keyboardTop = viewport.offsetTop + viewport.height;
    // The shrink of the visual viewport. Guard against small, non-keyboard
    // changes (URL bar collapse, or a map taller than the viewport) so the pane
    // only lifts for an actual keyboard.
    const keyboardHeight = window.innerHeight - keyboardTop;
    const overlap =
      keyboardHeight > 120 ? Math.max(0, this.getBoundingClientRect().bottom - keyboardTop) : 0;
    this.style.setProperty('--keyboard-inset', `${Math.round(overlap)}px`);
  };

  #controller = new AbortController();

  /** Re-measures the canvas when the container's size changes — the map hero
   *  settles through a few heights during load (see connectedCallback).
   *  @type {ResizeObserver | null} */
  #containerResizeObserver = null;

  /** The Mapbox map + library handle. */
  #map = null;
  #mapboxgl = null;

  /** True from the moment the library load is awaited until the map is built
   *  (or the load has failed) — the window during which document events are
   *  queued, see #untilMapReady. */
  #mapPending = false;
  /** Document events received during that window, replayed in order once it
   *  closes. @type {(() => void)[]} */
  #pendingEvents = [];

  /** Shop locations keyed by handle: { handle, coords, meta, marker, popup }. */
  #locationsByHandle = new Map();

  /** Grid-snapped `bb` boxes this map has already listed pins for (bar-less
   *  maps only — see #listOwnPins). @type {Set<string>} */
  #listedPinBoxes = new Set();

  /** Shop places already looked up, by handle, for the location branding
   *  fallback in #withShopFallback. `null` records a shop the index doesn't
   *  know, so it isn't asked for again. */
  #shopPlaces = new Map();

  /** Raw `data-demo-locations` the allow-list below was parsed from, so a
   *  re-read only re-parses when the attribute actually changed.
   *  @type {string | null | undefined} */
  #demoAllowRaw;
  /** @type {Set<string> | null} */
  #demoAllow = null;

  /** @type {AbortController | undefined} */
  #pinListingController;

  /** The map-search bar element driving the current results (called back for a
   *  location's products). @type {any} */
  #activeSearch = null;

  /** Handles of the shop locations that matched the current search (order
   *  preserved). Empty when there is no active search. @type {string[]} */
  #resultHandles = [];

  /** Whether a search currently drives the pins/pane — true from the first
   *  results/status event of a query until the cleared event. */
  #searchActive = false;

  /** The bar's mode-specific "nothing matched" headline for the current
   *  search, cached from its last results/status event. */
  #emptyText = '';

  /** The shop-registration prompt shown under an empty result, as the bar
   *  configured it — null when it sent none.
   *  @type {{ prompt: string, cta: string, url: string } | null} */
  #register = null;

  /** The matched handles of the active search as a Set — the pin filter while
   *  #searchActive. @type {Set<string> | null} */
  #resultHandleSet = null;

  /** The idle (no search ongoing) pin filter — the handles the bar's
   *  `/locations` listing allows, or null to show every pin (see
   *  #handlePinsChanged). @type {Set<string> | null} */
  #idlePinHandles = null;

  /** True while deferred pins (`data-pins-deferred`) await the bar's first
   *  idle listing: every pin is hidden.
   *  Cleared by the first `shopla:pins:changed` event or the
   *  DEFERRED_PINS_TIMEOUT failsafe. */
  #pinsPending = false;

  /** The full matched-location set of the current search (order preserved,
   *  pinned locations only). The pane renders the subset whose pins are in
   *  the viewport when #resultsFollowViewport is set.
   *  @type {{ handle: string, distance: number | null }[]} */
  #resultLocations = [];

  /** Whether the current results are viewport-filtered (location mode) and
   *  should follow the map as it moves. Shipped-mode results are not coupled
   *  to the viewport. */
  #resultsFollowViewport = true;

  /** The mode of the current search's results ('location' | 'shipping'). In
   *  shipping ("Shipping") mode the cards trade the opening-hours status for
   *  the "Ships to {region}" line — a card there means a matching product
   *  ships to the visitor's shipping region, so the hours of the physical
   *  door are not the point. */
  #searchMode = 'location';

  /** The shipping region's display name those "Ships to {region}" lines show
   *  — from the results event, kept fresh by `shopla:ships-to:changed`. ''
   *  while the bar hasn't resolved one; the line is then omitted. INVARIANT:
   *  always describes the zone the CURRENT result set was filtered by
   *  (#shipsToZone) — never a newer zone whose results are still in flight. */
  #shipsToRegion = '';

  /** The ships_to zone the current results were filtered by — what a
   *  `shopla:ships-to:changed` event's zone is checked against before its
   *  region name may relabel the cards in place. '' until a results event
   *  carries one. */
  #shipsToZone = '';

  /** The query behind the current results — stamped onto a carousel when it
   *  opens so a later re-emit can tell a pan (same query) from a refinement. */
  #currentQuery = '';

  /** Whether the merchant closed the results pane for the current query. While
   *  set, incoming results/status for the same query never auto-reopen the pane
   *  — only the toggle, or a query change, does. Reset on a new query, an
   *  explicit reopen, and when the search clears. */
  #resultsUserClosed = false;

  /** The reusable carousel popup + its shell/refs. */
  #carousel = null;
  #carouselEl = null;
  #carouselRefs = null;

  /** Carousel state: { kind, handle, shopHandle, query, mode, pages, page,
   *  hasMore, loading, superseded, loadSeq } or null. `kind` is 'location'
   *  (floats over a shop-location pin, keyed by `handle`) or 'delivery'
   *  (floats in the middle of the map, keyed by `shopHandle`; `handle` is
   *  null). `query` and `mode` stamp the search context the pages were
   *  rendered for, so a later re-emit can tell keep / refresh / reopen apart
   *  (see #handleSearchResults). `superseded` marks a page load the bar
   *  aborted under the carousel (`loading` stays set): the next results event
   *  refreshes it whatever its query (see #settleCarousel). */
  #carouselState = null;

  /** Centred overlay hosting the delivery carousel, built on first use. It is
   *  emptied rather than removed when the carousel closes, so the element is
   *  created at most once. @type {HTMLElement | null} */
  #centredCarouselHost = null;

  /** The shops with delivery-only matches in the current search — one card
   *  each — as `{ handle, rank }` in ranked order, where `rank` is the card's
   *  position among the shop-location cards. Empty outside delivery mode,
   *  where the service filters those docs out.
   *  @type {{ handle: string, rank: number }[]} */
  #deliveryShops = [];

  /** The pane's append-only display order for the current query+mode: card
   *  key (`loc:<location handle>` / `del:<shop handle>`) → the sequence it
   *  first rendered at. Once shown, a card keeps its relative position for
   *  the life of the search — later renders (results merged in by a pan, a
   *  pin panning back into view, a ships-to resolution) may only APPEND
   *  newcomers — so the list never reshuffles under the visitor and the
   *  pane's scroll position holds. Emptied when the query or the mode
   *  changes (a deliberately new result set ranks fresh) and when the
   *  search clears. @type {Map<string, number>} */
  #displayOrder = new Map();

  /** Monotonic stamp handed out to #displayOrder newcomers. */
  #displaySeq = 0;

  /** Shop branding by handle — title, url, logo and cover, so a delivery-only
   *  card looks like a shop-location card. Filled from the search results
   *  event, whose `delivery` entries carry the branding the bar resolved from
   *  the search service (see #resolveShopBranding in map-search.js). Kept
   *  across searches: a shop's branding doesn't change between queries.
   *  @type {Map<string, { handle: string, title?: string, url?: string, logo?: string, cover?: string }>} */
  #shopsByHandle = new Map();

  /** Set when a pan closed the carousel (its pin left the view) with nothing
   *  visible to hand it to — the next pan results that bring a match into
   *  view open its carousel. Any other open/close clears it. */
  #reopenBestOnPan = false;

  /** The right-click context menu ("Set as current location") + the clicked
   *  point. Built lazily on the first right-click. @type {HTMLElement | null} */
  #contextMenu = null;
  /** @type {{ lat: number, lng: number } | null} */
  #contextMenuLngLat = null;
  /** When the last right-drag rotation ended — the release of that drag fires
   *  a `contextmenu` on Windows, which must not open the menu. */
  #lastRotateEnd = 0;

  /** Open/closed status labels + locale for the location cards. */
  #labels = {};
  #locale = undefined;


  /**
   * Decides where the map opens (see "Initial view" above) — the `center` /
   * `zoom` or `bounds` / `fitBoundsOptions` the Map is constructed with, so
   * the first frame is already the right one and nothing moves afterwards.
   *
   * @param {any} mapboxgl
   * @param {any[]} locations - The section's seeded locations.
   * @returns {Promise<Record<string, any>>}
   */
  async #initialCamera(mapboxgl, locations) {
    const points = locations
      .map((/** @type any */ loc) => [Number(loc?.lng), Number(loc?.lat)])
      .filter(([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat));

    if (points.length === 1) return { center: points[0], zoom: DEFAULT_ZOOM };

    if (points.length > 1) {
      const bounds = new mapboxgl.LngLatBounds();
      for (const point of points) bounds.extend(point);
      return { bounds, fitBoundsOptions: { padding: FIT_PADDING, maxZoom: FIT_MAX_ZOOM } };
    }

    // Nothing seeded: the visitor's current location, bounded so a slow
    // resolution can't hold the map back indefinitely.
    const location = await Promise.race([
      resolveCurrentLocation({ searchUrl: this.dataset.searchUrl }),
      new Promise((resolve) => setTimeout(() => resolve(null), LOCATION_WAIT_MS)),
    ]);
    return { center: location ? [location.lng, location.lat] : DEFAULT_CENTER, zoom: DEFAULT_ZOOM };
  }

  /**
   * Whether the theme's own search bar is on the page and will deliver the
   * idle listing, with each location's card metadata — it never lists on a
   * shop-location page (`data-location-page`). Checked against the DOM, not
   * the upgraded element: the tag is in the markup whether or not its module
   * has run yet.
   */
  #themeBarDrivesPins() {
    return !!document.querySelector('map-search-component:not([data-location-page])');
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#containerResizeObserver?.disconnect();
    this.#containerResizeObserver = null;
    this.#controller.abort();
    // A load still in flight returns early on the aborted signal; nothing it
    // queued can apply to a map that will never be built.
    this.#mapPending = false;
    this.#pendingEvents = [];
  }

  get locations() {
    return this.dataset.locations;
  }
}

if (!customElements.get('mapbox-component')) {
  customElements.define('mapbox-component', Mapbox);
}
